import { mergeRefs, useDebounce, useRandomId, warnAboutMissingStyles } from '@entur/utils';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Downshift, { useSelect, useCombobox, useMultipleSelection } from 'downshift';
import classNames from 'classnames';
import { BaseFormControl, VariantProvider, FeedbackText, Checkbox } from '@entur/form';
import { CheckIcon, DownArrowIcon, CloseSmallIcon, CloseIcon } from '@entur/icons';
import { VisuallyHidden } from '@entur/a11y';
import { LoadingDots } from '@entur/loader';
import { space } from '@entur/tokens';
import { useFloating, autoUpdate, offset, flip } from '@floating-ui/react-dom';
import { IconButton } from '@entur/button';
import { TagChip } from '@entur/chip';
import { Tooltip } from '@entur/tooltip';
function _regeneratorRuntime() {
  _regeneratorRuntime = function () {
    return exports;
  };
  var exports = {},
    Op = Object.prototype,
    hasOwn = Op.hasOwnProperty,
    defineProperty = Object.defineProperty || function (obj, key, desc) {
      obj[key] = desc.value;
    },
    $Symbol = "function" == typeof Symbol ? Symbol : {},
    iteratorSymbol = $Symbol.iterator || "@@iterator",
    asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator",
    toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";
  function define(obj, key, value) {
    return Object.defineProperty(obj, key, {
      value: value,
      enumerable: !0,
      configurable: !0,
      writable: !0
    }), obj[key];
  }
  try {
    define({}, "");
  } catch (err) {
    define = function (obj, key, value) {
      return obj[key] = value;
    };
  }
  function wrap(innerFn, outerFn, self, tryLocsList) {
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator,
      generator = Object.create(protoGenerator.prototype),
      context = new Context(tryLocsList || []);
    return defineProperty(generator, "_invoke", {
      value: makeInvokeMethod(innerFn, self, context)
    }), generator;
  }
  function tryCatch(fn, obj, arg) {
    try {
      return {
        type: "normal",
        arg: fn.call(obj, arg)
      };
    } catch (err) {
      return {
        type: "throw",
        arg: err
      };
    }
  }
  exports.wrap = wrap;
  var ContinueSentinel = {};
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}
  var IteratorPrototype = {};
  define(IteratorPrototype, iteratorSymbol, function () {
    return this;
  });
  var getProto = Object.getPrototypeOf,
    NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  NativeIteratorPrototype && NativeIteratorPrototype !== Op && hasOwn.call(NativeIteratorPrototype, iteratorSymbol) && (IteratorPrototype = NativeIteratorPrototype);
  var Gp = GeneratorFunctionPrototype.prototype = Generator.prototype = Object.create(IteratorPrototype);
  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function (method) {
      define(prototype, method, function (arg) {
        return this._invoke(method, arg);
      });
    });
  }
  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);
      if ("throw" !== record.type) {
        var result = record.arg,
          value = result.value;
        return value && "object" == typeof value && hasOwn.call(value, "__await") ? PromiseImpl.resolve(value.__await).then(function (value) {
          invoke("next", value, resolve, reject);
        }, function (err) {
          invoke("throw", err, resolve, reject);
        }) : PromiseImpl.resolve(value).then(function (unwrapped) {
          result.value = unwrapped, resolve(result);
        }, function (error) {
          return invoke("throw", error, resolve, reject);
        });
      }
      reject(record.arg);
    }
    var previousPromise;
    defineProperty(this, "_invoke", {
      value: function (method, arg) {
        function callInvokeWithMethodAndArg() {
          return new PromiseImpl(function (resolve, reject) {
            invoke(method, arg, resolve, reject);
          });
        }
        return previousPromise = previousPromise ? previousPromise.then(callInvokeWithMethodAndArg, callInvokeWithMethodAndArg) : callInvokeWithMethodAndArg();
      }
    });
  }
  function makeInvokeMethod(innerFn, self, context) {
    var state = "suspendedStart";
    return function (method, arg) {
      if ("executing" === state) throw new Error("Generator is already running");
      if ("completed" === state) {
        if ("throw" === method) throw arg;
        return doneResult();
      }
      for (context.method = method, context.arg = arg;;) {
        var delegate = context.delegate;
        if (delegate) {
          var delegateResult = maybeInvokeDelegate(delegate, context);
          if (delegateResult) {
            if (delegateResult === ContinueSentinel) continue;
            return delegateResult;
          }
        }
        if ("next" === context.method) context.sent = context._sent = context.arg;else if ("throw" === context.method) {
          if ("suspendedStart" === state) throw state = "completed", context.arg;
          context.dispatchException(context.arg);
        } else "return" === context.method && context.abrupt("return", context.arg);
        state = "executing";
        var record = tryCatch(innerFn, self, context);
        if ("normal" === record.type) {
          if (state = context.done ? "completed" : "suspendedYield", record.arg === ContinueSentinel) continue;
          return {
            value: record.arg,
            done: context.done
          };
        }
        "throw" === record.type && (state = "completed", context.method = "throw", context.arg = record.arg);
      }
    };
  }
  function maybeInvokeDelegate(delegate, context) {
    var methodName = context.method,
      method = delegate.iterator[methodName];
    if (undefined === method) return context.delegate = null, "throw" === methodName && delegate.iterator.return && (context.method = "return", context.arg = undefined, maybeInvokeDelegate(delegate, context), "throw" === context.method) || "return" !== methodName && (context.method = "throw", context.arg = new TypeError("The iterator does not provide a '" + methodName + "' method")), ContinueSentinel;
    var record = tryCatch(method, delegate.iterator, context.arg);
    if ("throw" === record.type) return context.method = "throw", context.arg = record.arg, context.delegate = null, ContinueSentinel;
    var info = record.arg;
    return info ? info.done ? (context[delegate.resultName] = info.value, context.next = delegate.nextLoc, "return" !== context.method && (context.method = "next", context.arg = undefined), context.delegate = null, ContinueSentinel) : info : (context.method = "throw", context.arg = new TypeError("iterator result is not an object"), context.delegate = null, ContinueSentinel);
  }
  function pushTryEntry(locs) {
    var entry = {
      tryLoc: locs[0]
    };
    1 in locs && (entry.catchLoc = locs[1]), 2 in locs && (entry.finallyLoc = locs[2], entry.afterLoc = locs[3]), this.tryEntries.push(entry);
  }
  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal", delete record.arg, entry.completion = record;
  }
  function Context(tryLocsList) {
    this.tryEntries = [{
      tryLoc: "root"
    }], tryLocsList.forEach(pushTryEntry, this), this.reset(!0);
  }
  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) return iteratorMethod.call(iterable);
      if ("function" == typeof iterable.next) return iterable;
      if (!isNaN(iterable.length)) {
        var i = -1,
          next = function next() {
            for (; ++i < iterable.length;) if (hasOwn.call(iterable, i)) return next.value = iterable[i], next.done = !1, next;
            return next.value = undefined, next.done = !0, next;
          };
        return next.next = next;
      }
    }
    return {
      next: doneResult
    };
  }
  function doneResult() {
    return {
      value: undefined,
      done: !0
    };
  }
  return GeneratorFunction.prototype = GeneratorFunctionPrototype, defineProperty(Gp, "constructor", {
    value: GeneratorFunctionPrototype,
    configurable: !0
  }), defineProperty(GeneratorFunctionPrototype, "constructor", {
    value: GeneratorFunction,
    configurable: !0
  }), GeneratorFunction.displayName = define(GeneratorFunctionPrototype, toStringTagSymbol, "GeneratorFunction"), exports.isGeneratorFunction = function (genFun) {
    var ctor = "function" == typeof genFun && genFun.constructor;
    return !!ctor && (ctor === GeneratorFunction || "GeneratorFunction" === (ctor.displayName || ctor.name));
  }, exports.mark = function (genFun) {
    return Object.setPrototypeOf ? Object.setPrototypeOf(genFun, GeneratorFunctionPrototype) : (genFun.__proto__ = GeneratorFunctionPrototype, define(genFun, toStringTagSymbol, "GeneratorFunction")), genFun.prototype = Object.create(Gp), genFun;
  }, exports.awrap = function (arg) {
    return {
      __await: arg
    };
  }, defineIteratorMethods(AsyncIterator.prototype), define(AsyncIterator.prototype, asyncIteratorSymbol, function () {
    return this;
  }), exports.AsyncIterator = AsyncIterator, exports.async = function (innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    void 0 === PromiseImpl && (PromiseImpl = Promise);
    var iter = new AsyncIterator(wrap(innerFn, outerFn, self, tryLocsList), PromiseImpl);
    return exports.isGeneratorFunction(outerFn) ? iter : iter.next().then(function (result) {
      return result.done ? result.value : iter.next();
    });
  }, defineIteratorMethods(Gp), define(Gp, toStringTagSymbol, "Generator"), define(Gp, iteratorSymbol, function () {
    return this;
  }), define(Gp, "toString", function () {
    return "[object Generator]";
  }), exports.keys = function (val) {
    var object = Object(val),
      keys = [];
    for (var key in object) keys.push(key);
    return keys.reverse(), function next() {
      for (; keys.length;) {
        var key = keys.pop();
        if (key in object) return next.value = key, next.done = !1, next;
      }
      return next.done = !0, next;
    };
  }, exports.values = values, Context.prototype = {
    constructor: Context,
    reset: function (skipTempReset) {
      if (this.prev = 0, this.next = 0, this.sent = this._sent = undefined, this.done = !1, this.delegate = null, this.method = "next", this.arg = undefined, this.tryEntries.forEach(resetTryEntry), !skipTempReset) for (var name in this) "t" === name.charAt(0) && hasOwn.call(this, name) && !isNaN(+name.slice(1)) && (this[name] = undefined);
    },
    stop: function () {
      this.done = !0;
      var rootRecord = this.tryEntries[0].completion;
      if ("throw" === rootRecord.type) throw rootRecord.arg;
      return this.rval;
    },
    dispatchException: function (exception) {
      if (this.done) throw exception;
      var context = this;
      function handle(loc, caught) {
        return record.type = "throw", record.arg = exception, context.next = loc, caught && (context.method = "next", context.arg = undefined), !!caught;
      }
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i],
          record = entry.completion;
        if ("root" === entry.tryLoc) return handle("end");
        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc"),
            hasFinally = hasOwn.call(entry, "finallyLoc");
          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) return handle(entry.catchLoc, !0);
            if (this.prev < entry.finallyLoc) return handle(entry.finallyLoc);
          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) return handle(entry.catchLoc, !0);
          } else {
            if (!hasFinally) throw new Error("try statement without catch or finally");
            if (this.prev < entry.finallyLoc) return handle(entry.finallyLoc);
          }
        }
      }
    },
    abrupt: function (type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc <= this.prev && hasOwn.call(entry, "finallyLoc") && this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }
      finallyEntry && ("break" === type || "continue" === type) && finallyEntry.tryLoc <= arg && arg <= finallyEntry.finallyLoc && (finallyEntry = null);
      var record = finallyEntry ? finallyEntry.completion : {};
      return record.type = type, record.arg = arg, finallyEntry ? (this.method = "next", this.next = finallyEntry.finallyLoc, ContinueSentinel) : this.complete(record);
    },
    complete: function (record, afterLoc) {
      if ("throw" === record.type) throw record.arg;
      return "break" === record.type || "continue" === record.type ? this.next = record.arg : "return" === record.type ? (this.rval = this.arg = record.arg, this.method = "return", this.next = "end") : "normal" === record.type && afterLoc && (this.next = afterLoc), ContinueSentinel;
    },
    finish: function (finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) return this.complete(entry.completion, entry.afterLoc), resetTryEntry(entry), ContinueSentinel;
      }
    },
    catch: function (tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;
          if ("throw" === record.type) {
            var thrown = record.arg;
            resetTryEntry(entry);
          }
          return thrown;
        }
      }
      throw new Error("illegal catch attempt");
    },
    delegateYield: function (iterable, resultName, nextLoc) {
      return this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      }, "next" === this.method && (this.arg = undefined), ContinueSentinel;
    }
  }, exports;
}
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }
  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}
function _asyncToGenerator(fn) {
  return function () {
    var self = this,
      args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);
      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }
      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }
      _next(undefined);
    });
  };
}
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectDestructuringEmpty(obj) {
  if (obj == null) throw new TypeError("Cannot destructure " + obj);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var _excluded$d = ["children", "onChange", "onInputValueChange", "highlightFirstItemOnOpen", "className", "style", "searchable"];
var DownshiftContext = /*#__PURE__*/React.createContext(null);
var DownshiftProvider = function DownshiftProvider(_ref) {
  var children = _ref.children,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? function () {
      return undefined;
    } : _ref$onChange,
    _ref$onInputValueChan = _ref.onInputValueChange,
    onInputValueChange = _ref$onInputValueChan === void 0 ? function () {
      return undefined;
    } : _ref$onInputValueChan,
    _ref$highlightFirstIt = _ref.highlightFirstItemOnOpen,
    highlightFirstItemOnOpen = _ref$highlightFirstIt === void 0 ? false : _ref$highlightFirstIt,
    className = _ref.className,
    style = _ref.style,
    _ref$searchable = _ref.searchable,
    searchable = _ref$searchable === void 0 ? false : _ref$searchable,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$d);
  var handleStateChange = function handleStateChange(changes, stateAndHelpers) {
    if (changes.type === Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem) {
      return;
    }
    if ('selectedItem' in changes) {
      onChange(changes.selectedItem, stateAndHelpers);
    } else if ('inputValue' in changes) {
      onInputValueChange(changes.inputValue);
    }
  };
  var stateReducer = function stateReducer(_, changes) {
    var highlightFirstOnOpen = highlightFirstItemOnOpen && 'isOpen' in changes && changes.isOpen;
    var highlightFirstItemIndex = highlightFirstOnOpen ? {
      highlightedIndex: 0
    } : {};
    if (searchable) {
      var type = changes.type;
      switch (type) {
        case '__autocomplete_change_input__':
          return _extends({}, changes, highlightFirstItemIndex);
        case '__autocomplete_click_item__':
        case '__autocomplete_keydown_enter__':
        case '__autocomplete_blur_input__':
        case '__autocomplete_mouseup__':
          return _extends({}, changes, {
            // if we had an item highlighted in the previous state.
            // we will reset input field value to blank
            inputValue: ''
          }, highlightFirstItemIndex);
        default:
          return _extends({}, changes, highlightFirstItemIndex);
      }
    } else {
      return _extends({}, changes, highlightFirstItemIndex);
    }
  };
  return React.createElement(Downshift, _extends({
    itemToString: function itemToString(item) {
      return item ? item.label : '';
    },
    onStateChange: handleStateChange,
    stateReducer: stateReducer
  }, rest), function (args) {
    return React.createElement("div", {
      className: classNames('eds-input-group', className),
      style: style
    }, React.createElement(DownshiftContext.Provider, {
      value: args
    }, children));
  });
};
var useDownshift = function useDownshift() {
  var context = React.useContext(DownshiftContext);
  if (!context) {
    throw new Error('You need to wrap your component in a DownshiftProvider');
  }
  return context;
};
var _excluded$c = ["items"];
var DropdownListDeprecated = function DropdownListDeprecated(_ref) {
  var items = _ref.items,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$c);
  var _useDownshift = useDownshift(),
    highlightedIndex = _useDownshift.highlightedIndex,
    isOpen = _useDownshift.isOpen,
    selectedItem = _useDownshift.selectedItem,
    getItemProps = _useDownshift.getItemProps,
    getMenuProps = _useDownshift.getMenuProps;
  return React.createElement("ul", _extends({
    className: classNames('eds-dropdown-list', {
      'eds-dropdown-list--open': isOpen
    })
  }, getMenuProps(), rest), isOpen ? items.map(function (item, index) {
    return (
      // eslint-disable-next-line react/jsx-key
      React.createElement("li", _extends({
        className: classNames('eds-dropdown-list__item', {
          'eds-dropdown-list__item--highlighted': highlightedIndex === index,
          'eds-dropdown-list__item--selected': selectedItem === item
        })
      }, getItemProps({
        key: "" + index + item.value,
        item: item,
        index: index
      })), React.createElement("span", null, item.label), item.icons && React.createElement("span", null, item.icons.map(function (Icon, index) {
        return React.createElement(Icon, {
          key: index,
          inline: true,
          className: "eds-dropdown-list__item-icon"
        });
      })), selectedItem === item && React.createElement(CheckIcon, null))
    );
  }) : null);
};
var DropdownDeprecatedToggleButton = function DropdownDeprecatedToggleButton() {
  var _useDownshift = useDownshift(),
    getToggleButtonProps = _useDownshift.getToggleButtonProps,
    isOpen = _useDownshift.isOpen;
  return React.createElement("button", _extends({}, getToggleButtonProps({
    className: classNames('eds-dropdown__toggle-button', {
      'eds-dropdown__toggle-button--open': isOpen
    })
  }), {
    tabIndex: -1,
    type: "button"
  }), React.createElement(DownArrowIcon, null));
};
var _excluded$b = ["className", "children"];
var DropdownDeprecatedLoadingDots = function DropdownDeprecatedLoadingDots(_ref) {
  var className = _ref.className,
    children = _ref.children,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$b);
  return React.createElement("div", _extends({
    className: classNames('eds-inline-spinner', className)
  }, rest), React.createElement(LoadingDots, null), React.createElement(VisuallyHidden, null, children));
};
var _excluded$a = ["children", "className", "items", "loading", "loadingText", "placeholder", "style", "listStyle", "clearable", "label", "isFilled", "disableLabelAnimation"];
var BaseDropdownDeprecated = function BaseDropdownDeprecated(_ref) {
  var children = _ref.children,
    className = _ref.className,
    items = _ref.items,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? false : _ref$loading,
    _ref$loadingText = _ref.loadingText,
    loadingText = _ref$loadingText === void 0 ? 'Loading...' : _ref$loadingText,
    style = _ref.style,
    listStyle = _ref.listStyle,
    clearable = _ref.clearable,
    label = _ref.label,
    _ref$isFilled = _ref.isFilled,
    isFilled = _ref$isFilled === void 0 ? false : _ref$isFilled,
    disableLabelAnimation = _ref.disableLabelAnimation,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$a);
  var _useDownshift = useDownshift(),
    getLabelProps = _useDownshift.getLabelProps;
  return React.createElement("div", {
    className: "eds-dropdown-wrapper",
    style: style
  }, React.createElement(BaseFormControl, _extends({
    append: React.createElement(Appendix$1, {
      clearable: clearable,
      loading: loading,
      loadingText: loadingText,
      readOnly: rest.readOnly
    }),
    className: className,
    label: label,
    isFilled: isFilled,
    labelProps: getLabelProps(),
    disableLabelAnimation: disableLabelAnimation
  }, rest), children), React.createElement(DropdownListDeprecated, _extends({
    items: items,
    style: _extends({
      position: 'absolute',
      top: space.extraLarge3 + space.extraSmall + "px"
    }, listStyle)
  }, rest)));
};
var ClearButton$1 = function ClearButton(_ref2) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref2), _ref2));
  var _useDownshift2 = useDownshift(),
    clearSelection = _useDownshift2.clearSelection,
    selectedItem = _useDownshift2.selectedItem;
  return React.createElement(React.Fragment, null, selectedItem && React.createElement("button", _extends({
    className: "eds-dropdown__clear-button",
    type: "button",
    tabIndex: -1,
    onClick: function onClick() {
      return clearSelection();
    },
    "aria-label": "Trykk for \xE5 fjerne valg"
  }, props), React.createElement(CloseSmallIcon, null)), selectedItem && React.createElement("div", {
    className: "eds-dropdown__divider"
  }));
};
var Appendix$1 = function Appendix(_ref3) {
  var clearable = _ref3.clearable,
    loading = _ref3.loading,
    loadingText = _ref3.loadingText,
    readOnly = _ref3.readOnly;
  if (loading) {
    return React.createElement(DropdownDeprecatedLoadingDots, null, loadingText);
  }
  if (readOnly) {
    return null;
  }
  return clearable ? React.createElement(React.Fragment, null, React.createElement(ClearButton$1, null), " ", React.createElement(DropdownDeprecatedToggleButton, null)) : React.createElement(DropdownDeprecatedToggleButton, null);
};
var _excluded$9 = ["disabled", "placeholder", "selectOnTab", "openOnFocus", "listStyle", "items", "label", "disableLabelAnimation", "loading", "loadingText", "className", "clearable"];
var RegularDropdownDeprecated = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var disabled = _ref.disabled,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? 'Vennligst velg' : _ref$placeholder,
    _ref$selectOnTab = _ref.selectOnTab,
    selectOnTab = _ref$selectOnTab === void 0 ? false : _ref$selectOnTab,
    _ref$openOnFocus = _ref.openOnFocus,
    openOnFocus = _ref$openOnFocus === void 0 ? false : _ref$openOnFocus,
    listStyle = _ref.listStyle,
    items = _ref.items,
    label = _ref.label,
    disableLabelAnimation = _ref.disableLabelAnimation,
    loading = _ref.loading,
    loadingText = _ref.loadingText,
    className = _ref.className,
    clearable = _ref.clearable,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$9);
  var _useDownshift = useDownshift(),
    getToggleButtonProps = _useDownshift.getToggleButtonProps,
    selectedItem = _useDownshift.selectedItem,
    selectHighlightedItem = _useDownshift.selectHighlightedItem,
    openMenu = _useDownshift.openMenu,
    isOpen = _useDownshift.isOpen,
    highlightedIndex = _useDownshift.highlightedIndex,
    setHighlightedIndex = _useDownshift.setHighlightedIndex;
  return React.createElement(BaseDropdownDeprecated, _extends({
    disabled: disabled,
    listStyle: listStyle,
    items: items,
    label: label,
    isFilled: selectedItem ? true : placeholder.length !== 0,
    disableLabelAnimation: disableLabelAnimation,
    loading: loading,
    loadingText: loadingText,
    className: className,
    clearable: clearable
  }, rest), React.createElement("button", _extends({}, getToggleButtonProps(_extends({
    className: 'eds-form-control eds-dropdown__selected-item',
    style: {
      textAlign: 'left'
    },
    disabled: disabled,
    type: 'button',
    onKeyDown: function onKeyDown(e) {
      if (selectOnTab && e.key === 'Tab') {
        selectHighlightedItem();
      }
      if (isOpen) {
        var keyDownValue = e.key;
        var matchedItems = items.map(function (item, index) {
          return _extends({}, item, {
            index: index
          });
        }).filter(function (item) {
          var firstCharacter = item.label.trim().charAt(0).toLowerCase();
          return firstCharacter === keyDownValue;
        });
        var nextHighlightItem = matchedItems.find(function (item) {
          return item.index > (highlightedIndex != null ? highlightedIndex : 0);
        });
        if (nextHighlightItem) {
          setHighlightedIndex(nextHighlightItem.index);
        } else if (matchedItems.length > 0) {
          setHighlightedIndex(matchedItems[0].index);
        }
      }
    },
    onFocus: function onFocus() {
      if (openOnFocus) {
        !isOpen && openMenu();
      }
    }
  }, rest)), {
    ref: ref
  }), selectedItem ? selectedItem.label : placeholder));
});
var _excluded$8 = ["disabled", "className", "items", "loading", "loadingText", "readOnly", "prepend", "selectOnTab", "openOnFocus", "listStyle", "clearable", "itemFilter", "label", "disableLabelAnimation", "placeholder"];
function LowerCaseFilterTest(item, input) {
  if (!input) {
    return true;
  }
  var sanitizeEscapeCharacters = input.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  var inputRegex = new RegExp(sanitizeEscapeCharacters, 'i');
  return inputRegex.test(item.label);
}
/**
 * @deprecated
 * New SearchableDropdown available
 *
 * migration guide to new dropdown:
 * - change import from 'DropdownDeprecated' to 'Dropdown'
 * - change from 'value' to 'selectedItem'
 *  + type of 'selectedItem' is 'NormalizedDropdownType' and does not include 'string'
 * - 'onChange' must update value of 'selectedItem'
 */
var SearchableDropdownDeprecated = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    className = _ref.className,
    items = _ref.items,
    loading = _ref.loading,
    loadingText = _ref.loadingText,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    prepend = _ref.prepend,
    _ref$selectOnTab = _ref.selectOnTab,
    selectOnTab = _ref$selectOnTab === void 0 ? false : _ref$selectOnTab,
    _ref$openOnFocus = _ref.openOnFocus,
    openOnFocus = _ref$openOnFocus === void 0 ? false : _ref$openOnFocus,
    listStyle = _ref.listStyle,
    clearable = _ref.clearable,
    _ref$itemFilter = _ref.itemFilter,
    itemFilter = _ref$itemFilter === void 0 ? function (item, inputValue) {
      return LowerCaseFilterTest(item, inputValue);
    } : _ref$itemFilter,
    label = _ref.label,
    disableLabelAnimation = _ref.disableLabelAnimation,
    placeholder = _ref.placeholder,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$8);
  var _useDownshift = useDownshift(),
    getInputProps = _useDownshift.getInputProps,
    inputValue = _useDownshift.inputValue,
    selectHighlightedItem = _useDownshift.selectHighlightedItem,
    isOpen = _useDownshift.isOpen,
    openMenu = _useDownshift.openMenu,
    closeMenu = _useDownshift.closeMenu,
    selectedItem = _useDownshift.selectedItem;
  var _useState = useState(false),
    hideSelectedItem = _useState[0],
    setHideSelectedItem = _useState[1];
  var inputRef = useRef(null);
  var filteredItems = React.useMemo(function () {
    return items.filter(function (item) {
      return itemFilter(item, inputValue);
    });
  }, [inputValue, items, itemFilter]);
  return React.createElement(BaseDropdownDeprecated, {
    items: filteredItems,
    disabled: disabled,
    readOnly: readOnly,
    className: className,
    loading: loading,
    loadingText: loadingText,
    prepend: prepend,
    listStyle: listStyle,
    clearable: clearable,
    label: label,
    isFilled: selectedItem ? true : false,
    disableLabelAnimation: disableLabelAnimation
  }, !hideSelectedItem && selectedItem && !inputValue && React.createElement("span", {
    className: "eds-dropdown__searchable-selected-item__wrapper"
  }, React.createElement("span", {
    className: "eds-dropdown__searchable-selected-item",
    onClick: function onClick() {
      var _inputRef$current;
      return (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.focus();
    }
  }, selectedItem.label)), React.createElement("input", _extends({}, getInputProps(_extends({
    disabled: disabled,
    readOnly: readOnly,
    className: 'eds-form-control eds-dropdown__input',
    onKeyDown: function onKeyDown(e) {
      if (selectOnTab && e.key === 'Tab') selectHighlightedItem();
    },
    onFocus: function onFocus() {
      if (!isOpen && openOnFocus) openMenu();
      setHideSelectedItem(true);
    },
    placeholder: selectedItem ? selectedItem.label : placeholder
  }, rest)), {
    onBlur: function onBlur() {
      setHideSelectedItem(false);
      closeMenu();
    },
    ref: mergeRefs(ref, inputRef)
  })));
});
var DropdownInputGroupDeprecated = function DropdownInputGroupDeprecated(_ref) {
  var children = _ref.children,
    feedback = _ref.feedback,
    variant = _ref.variant,
    className = _ref.className,
    style = _ref.style;
  return React.createElement(VariantProvider, {
    variant: variant
  }, React.createElement("div", {
    className: className,
    style: style
  }, children, feedback && variant && React.createElement(FeedbackText, {
    variant: variant
  }, feedback)));
};
var useNormalizedItems = function useNormalizedItems(items) {
  return React.useMemo(function () {
    return items.map(function (item) {
      if (typeof item == 'string') {
        return {
          value: item,
          label: item
        };
      }
      if ((item == null ? void 0 : item.value) === undefined) {
        return _extends({}, item, {
          value: item.label
        });
      }
      return _extends({}, item, {
        value: item.value
      });
    });
  }, [items]);
};
var useResolvedItems = function useResolvedItems(/** The list of items, or an async function that resolves the list of items */
itemsOrItemsResolver, /** The time to wait after the input changes to the fetchItems method is called */
debounceTimeout) {
  if (debounceTimeout === void 0) {
    debounceTimeout = 250;
  }
  var itemsIsAFunction = typeof itemsOrItemsResolver === 'function';
  var _React$useState = React.useState(itemsIsAFunction ? [] : itemsOrItemsResolver),
    items = _React$useState[0],
    setItems = _React$useState[1];
  var _React$useState2 = React.useState(false),
    loading = _React$useState2[0],
    setLoading = _React$useState2[1];
  var abortControllerRef = React.useRef(new AbortController());
  // We normalize the itemsResolver argument to an async function, so we
  // can use it without thinking about the differences later
  var itemsResolver = React.useMemo(function () {
    if (itemsIsAFunction) return itemsOrItemsResolver;
    return function () {
      return Promise.resolve(itemsOrItemsResolver);
    };
  }, [itemsOrItemsResolver, itemsIsAFunction]);
  // This should be called whenever the input value changes
  var updateItems = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee(inputValue) {
      var _abortControllerRef$c;
      var abortController, _abortControllerRef$c2, _abortControllerRef$c3, resolvedItems;
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              // The abortController handles cleanup of the previous request and unmounting
              if (abortControllerRef != null && abortControllerRef.current) abortControllerRef == null ? void 0 : (_abortControllerRef$c = abortControllerRef.current) == null ? void 0 : _abortControllerRef$c.abort();
              abortController = new AbortController();
              abortControllerRef.current = abortController;
              setLoading(true);
              _context.prev = 4;
              _context.next = 7;
              return itemsResolver(inputValue != null ? inputValue : '', abortControllerRef);
            case 7:
              resolvedItems = _context.sent;
              if (!(abortControllerRef != null && (_abortControllerRef$c2 = abortControllerRef.current) != null && (_abortControllerRef$c3 = _abortControllerRef$c2.signal) != null && _abortControllerRef$c3.aborted)) {
                _context.next = 11;
                break;
              }
              console.warn('Avbryt den asynkrone funksjonen din med signalet fra AbortController-en for å for å unngå minnelekkasje.', 'Funksjonen bør kaste en DOMException med navnet "AbortError" når den avbrytes.', '', '\n\nSe eksempel her: https://design.entur.no/komponenter/skjemaelementer/dropdown#s%C3%B8kbar-dropdown-med-valg-fra-nettverkskall-bassert-p%C3%A5-tekstinput', '\nLes mer om AbortController her: https://developer.mozilla.org/en-US/docs/Web/API/AbortController');
              return _context.abrupt("return");
            case 11:
              setLoading(false);
              setItems(resolvedItems);
              _context.next = 21;
              break;
            case 15:
              _context.prev = 15;
              _context.t0 = _context["catch"](4);
              if (!(_context.t0 && typeof _context.t0 === 'object' && 'name' in _context.t0 && _context.t0.name === 'AbortError')) {
                _context.next = 19;
                break;
              }
              return _context.abrupt("return");
            case 19:
              console.warn('The following error was received but not handled inside Entur Designsystems useResolvedItems hook:');
              throw _context.t0;
            case 21:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[4, 15]]);
    }));
    return function updateItems(_x) {
      return _ref.apply(this, arguments);
    };
  }();
  var debouncedFetchItems = useDebounce(updateItems, debounceTimeout);
  var normalizedItems = useNormalizedItems(items);
  React.useEffect(function () {
    // send abort signal to previous request on unmount for cleanup
    return function () {
      var _abortControllerRef$c4;
      return abortControllerRef == null ? void 0 : (_abortControllerRef$c4 = abortControllerRef.current) == null ? void 0 : _abortControllerRef$c4.abort('Component unmounted');
    };
  }, []);
  React.useEffect(function () {
    // Let's fetch the list initially if it's specified
    if (itemsIsAFunction) {
      debouncedFetchItems('');
    }
  }, [itemsIsAFunction, itemsResolver]);
  return {
    items: normalizedItems,
    loading: itemsIsAFunction ? loading : false,
    fetchItems: debouncedFetchItems
  };
};
var _excluded$7 = ["highlightFirstItemOnOpen", "debounceTimeout", "disabled", "feedback", "items", "label", "loadingText", "onChange", "placeholder", "prepend", "readOnly", "searchable", "selectOnTab", "openOnFocus", "variant", "value", "clearable", "className", "style", "listStyle", "itemFilter", "disableLabelAnimation"];
/**
 * @deprecated
 * New Dropdown available
 *
 * migration guide to new dropdown:
 * - change import from 'DropdownDeprecated' to 'Dropdown'
 *  + if 'searchable', switch to 'SearchableDropdown'
 * - change from 'value' to 'selectedItem'
 *  + type of 'selectedItem' is 'NormalizedDropdownType' and does not include 'string'
 * - 'onChange' must update value of 'selectedItem'
 */
var DropdownDeprecated = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var highlightFirstItemOnOpen = _ref.highlightFirstItemOnOpen,
    debounceTimeout = _ref.debounceTimeout,
    disabled = _ref.disabled,
    feedback = _ref.feedback,
    items = _ref.items,
    label = _ref.label,
    loadingText = _ref.loadingText,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? function () {
      return undefined;
    } : _ref$onChange,
    placeholder = _ref.placeholder,
    prepend = _ref.prepend,
    readOnly = _ref.readOnly,
    searchable = _ref.searchable,
    selectOnTab = _ref.selectOnTab,
    openOnFocus = _ref.openOnFocus,
    variant = _ref.variant,
    value = _ref.value,
    _ref$clearable = _ref.clearable,
    clearable = _ref$clearable === void 0 ? false : _ref$clearable,
    className = _ref.className,
    style = _ref.style,
    listStyle = _ref.listStyle,
    itemFilter = _ref.itemFilter,
    disableLabelAnimation = _ref.disableLabelAnimation,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$7);
  var _useResolvedItems = useResolvedItems(items, debounceTimeout),
    normalizedItems = _useResolvedItems.items,
    loading = _useResolvedItems.loading,
    fetchItems = _useResolvedItems.fetchItems;
  var selectedItem = value === undefined ? undefined : normalizedItems.find(function (item) {
    return value === item.value;
  }) || null;
  var RenderedDropdown = searchable ? SearchableDropdownDeprecated : RegularDropdownDeprecated;
  var searchAbleProps = searchable ? {
    itemFilter: itemFilter,
    name: rest.name,
    'data-cy': rest['data-cy']
  } : {
    name: rest.name,
    'data-cy': rest['data-cy']
  };
  return React.createElement(DownshiftProvider, _extends({
    selectedItem: selectedItem,
    onInputValueChange: fetchItems,
    onChange: onChange,
    value: value,
    highlightFirstItemOnOpen: highlightFirstItemOnOpen,
    className: className,
    style: style,
    searchable: searchable
  }, rest), React.createElement(DropdownInputGroupDeprecated, {
    feedback: feedback,
    variant: variant
  }, React.createElement(RenderedDropdown, _extends({
    label: label,
    items: normalizedItems,
    loading: loading,
    loadingText: loadingText,
    disabled: disabled,
    readOnly: readOnly,
    placeholder: placeholder,
    prepend: prepend,
    selectOnTab: selectOnTab,
    openOnFocus: openOnFocus,
    listStyle: listStyle,
    clearable: clearable,
    disableLabelAnimation: disableLabelAnimation,
    ref: ref
  }, searchAbleProps))));
});
var _excluded$6 = ["className", "clearable", "debounceTimeout", "disabled", "feedback", "initialSelectedItems", "items", "itemsSelectedLabel", "label", "listStyle", "loading", "loadingText", "onChange", "openOnFocus", "readOnly", "style", "variant"];
var MultiSelectOldContext = /*#__PURE__*/React.createContext(null);
var useMultiSelectOldContext = function useMultiSelectOldContext() {
  var context = React.useContext(MultiSelectOldContext);
  if (!context) {
    throw new Error('You need to wrap your component in a DownshiftProvider');
  }
  return context;
};
function stateReducer(state,
//StateChangeOptions<NormalizedDropdownItemDeprecatedType>,
actionAndChanges) {
  var changes = actionAndChanges.changes,
    type = actionAndChanges.type;
  switch (type) {
    case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
    case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
    case useSelect.stateChangeTypes.ItemClick:
      return _extends({}, changes, {
        isOpen: true,
        highlightedIndex: state.highlightedIndex
      });
    default:
      return changes;
  }
}
/**
 * @deprecated
 * New MultiSelect available
 *
 * migration guide to new dropdown:
 * - change import from 'MultiSelectDeprecated' to 'MultiSelect'
 * - add state containing selected items to 'selectedItems' prop
 *  + type of 'selectedItems' is 'NormalizedDropdownType[]' and does not include 'string'
 * - 'onChange' must update value of 'selectedItem'
 */
var MultiSelectDeprecated = function MultiSelectDeprecated(_ref) {
  var className = _ref.className,
    _ref$clearable = _ref.clearable,
    clearable = _ref$clearable === void 0 ? false : _ref$clearable,
    debounceTimeout = _ref.debounceTimeout,
    disabled = _ref.disabled,
    feedback = _ref.feedback,
    _ref$initialSelectedI = _ref.initialSelectedItems,
    initialSelectedItems = _ref$initialSelectedI === void 0 ? [] : _ref$initialSelectedI,
    input = _ref.items,
    _ref$itemsSelectedLab = _ref.itemsSelectedLabel,
    itemsSelectedLabel = _ref$itemsSelectedLab === void 0 ? function (items) {
      return SelectedItemsLabel(items);
    } : _ref$itemsSelectedLab,
    label = _ref.label,
    listStyle = _ref.listStyle,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? false : _ref$loading,
    _ref$loadingText = _ref.loadingText,
    loadingText = _ref$loadingText === void 0 ? '' : _ref$loadingText,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? function () {
      return undefined;
    } : _ref$onChange,
    _ref$openOnFocus = _ref.openOnFocus,
    openOnFocus = _ref$openOnFocus === void 0 ? false : _ref$openOnFocus,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    style = _ref.style,
    variant = _ref.variant,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$6);
  var _useResolvedItems = useResolvedItems(input, debounceTimeout),
    items = _useResolvedItems.items;
  var _useState = useState(initialSelectedItems),
    selectedItems = _useState[0],
    setSelectedItems = _useState[1];
  var reset = React.useCallback(function () {
    setSelectedItems([]);
  }, []);
  function isSelected(selectedCheckboxItem) {
    return selectedItems.some(function (selected) {
      return selected.value === selectedCheckboxItem.value;
    });
  }
  var _useSelect = useSelect(_extends({
      items: items,
      stateReducer: stateReducer,
      selectedItem: null,
      onSelectedItemChange: function onSelectedItemChange(_ref2) {
        var selectedItem = _ref2.selectedItem;
        if (!selectedItem) {
          return;
        }
        var itemIsFound = isSelected(selectedItem);
        if (itemIsFound) {
          var slicedItemList = selectedItems.filter(function (item) {
            return item.value !== selectedItem.value;
          });
          setSelectedItems(slicedItemList);
          onChange(slicedItemList);
        } else {
          var _slicedItemList = [].concat(selectedItems, [selectedItem]);
          setSelectedItems(_slicedItemList);
          onChange(_slicedItemList);
        }
      }
    }, rest)),
    isOpen = _useSelect.isOpen,
    getToggleButtonProps = _useSelect.getToggleButtonProps,
    getLabelProps = _useSelect.getLabelProps,
    getMenuProps = _useSelect.getMenuProps,
    highlightedIndex = _useSelect.highlightedIndex,
    getItemProps = _useSelect.getItemProps,
    openMenu = _useSelect.openMenu;
  var buttonText = selectedItems.length ? itemsSelectedLabel(selectedItems) : '';
  var multiSelectId = useRandomId('eds-multiselect');
  return React.createElement(MultiSelectOldContext.Provider, {
    value: {
      isOpen: isOpen,
      reset: reset,
      getToggleButtonProps: getToggleButtonProps,
      openMenu: openMenu,
      openOnFocus: openOnFocus
    }
  }, React.createElement("div", {
    className: classNames('eds-multiselect', 'eds-dropdown-wrapper', className),
    style: style
  }, React.createElement(BaseFormControl, {
    label: label,
    labelId: multiSelectId,
    labelProps: getLabelProps,
    feedback: feedback,
    variant: variant,
    isFilled: selectedItems.length > 0 || isOpen,
    disabled: disabled,
    readOnly: readOnly,
    append: React.createElement(Appendix, {
      hasSelected: clearable && selectedItems.length > 0,
      loading: loading,
      loadingText: loadingText,
      readOnly: readOnly
    })
  }, React.createElement("button", _extends({}, getToggleButtonProps({
    style: {
      textAlign: 'left'
    },
    type: 'button',
    className: 'eds-form-control eds-multiselect__button'
  })), buttonText)), React.createElement("ul", _extends({
    className: classNames('eds-dropdown-list', {
      'eds-dropdown-list--open': isOpen
    })
  }, getMenuProps({
    style: _extends({
      position: 'absolute',
      top: space.extraLarge3 + space.extraSmall + "px"
    }, listStyle)
  })), isOpen && items.map(function (item, index) {
    return React.createElement("li", _extends({
      className: classNames('eds-dropdown-list__item', {
        'eds-dropdown-list__item--highlighted': highlightedIndex === index,
        'eds-dropdown-list__item--selected': isSelected(item)
      }),
      key: "" + item.value + index
    }, getItemProps({
      item: item,
      index: index
    }), {
      style: {
        display: 'flex'
      }
    }), React.createElement("span", {
      style: {
        display: 'flex'
      }
    }, React.createElement("span", {
      className: classNames('eds-multiselect-checkbox', {
        'eds-multiselect-checkbox--checked': isSelected(item)
      })
    }, React.createElement(CheckboxIcon, null)), React.createElement("span", {
      className: "eds-multiselect__item-label"
    }, item.label)), item.icons && React.createElement("span", null, item.icons.map(function (Icon, index) {
      return React.createElement(Icon, {
        key: index,
        inline: true,
        className: "eds-dropdown-list__item-icon"
      });
    })));
  }))));
};
var ClearButton = function ClearButton(_ref3) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref3), _ref3));
  var _useMultiSelectOldCon = useMultiSelectOldContext(),
    reset = _useMultiSelectOldCon.reset;
  return React.createElement(React.Fragment, null, React.createElement("button", _extends({
    className: "eds-dropdown__clear-button",
    type: "button",
    tabIndex: -1,
    onClick: function onClick() {
      return reset();
    }
  }, props), React.createElement(CloseIcon, null)), React.createElement("div", {
    className: "eds-dropdown__divider"
  }));
};
var Appendix = function Appendix(_ref4) {
  var loading = _ref4.loading,
    loadingText = _ref4.loadingText,
    readOnly = _ref4.readOnly,
    hasSelected = _ref4.hasSelected;
  if (loading) {
    return React.createElement(DropdownDeprecatedLoadingDots, null, loadingText);
  }
  if (readOnly) {
    return null;
  }
  return hasSelected ? React.createElement(React.Fragment, null, React.createElement(ClearButton, null), React.createElement(DropdownToggleButton, null)) : React.createElement(DropdownToggleButton, null);
};
var DropdownToggleButton = function DropdownToggleButton() {
  var _useMultiSelectOldCon2 = useMultiSelectOldContext(),
    getToggleButtonProps = _useMultiSelectOldCon2.getToggleButtonProps,
    isOpen = _useMultiSelectOldCon2.isOpen,
    openMenu = _useMultiSelectOldCon2.openMenu,
    openOnFocus = _useMultiSelectOldCon2.openOnFocus;
  return React.createElement("button", _extends({}, getToggleButtonProps({
    className: classNames('eds-dropdown__toggle-button', {
      'eds-dropdown__toggle-button--open': isOpen
    }),
    onFocus: function onFocus() {
      if (openOnFocus) {
        openMenu();
      }
    }
  }), {
    type: "button"
  }), React.createElement(DownArrowIcon, null));
};
var CheckboxIcon = function CheckboxIcon() {
  return React.createElement("svg", {
    className: "eds-checkbox-icon",
    width: "11px",
    height: "9px",
    viewBox: "6 11 37 33"
  }, React.createElement("path", {
    className: "eds-checkbox-icon__path",
    d: "M14.1 27.2l7.1 7.2 14.6-14.8",
    fill: "none"
  }));
};
function SelectedItemsLabel(items) {
  return items.length < 3 ? items.map(function (item) {
    return item.label;
  }).toString() : items.length + " elementer valgt";
}
var _excluded$5 = ["ariaLabelChosenSingular", "ariaLabelSelectedItem", "getItemProps", "getMenuProps", "inputValue", "isOpen", "highlightedIndex", "listItems", "listStyle", "listRef", "loading", "loadingText", "noMatchesText", "selectAllCheckboxState", "selectAllItem", "selectedItems", "showSelectAllInList"];
var DropdownList = function DropdownList(_ref) {
  var _listItems$;
  var _ref$ariaLabelChosenS = _ref.ariaLabelChosenSingular,
    ariaLabelChosenSingular = _ref$ariaLabelChosenS === void 0 ? 'valgt' : _ref$ariaLabelChosenS,
    _ref$ariaLabelSelecte = _ref.ariaLabelSelectedItem,
    ariaLabelSelectedItem = _ref$ariaLabelSelecte === void 0 ? ', valgt element, trykk for å fjerne' : _ref$ariaLabelSelecte,
    getItemProps = _ref.getItemProps,
    getMenuProps = _ref.getMenuProps,
    isOpen = _ref.isOpen,
    highlightedIndex = _ref.highlightedIndex,
    listItems = _ref.listItems,
    listStyle = _ref.listStyle,
    listRef = _ref.listRef,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? false : _ref$loading,
    _ref$loadingText = _ref.loadingText,
    loadingText = _ref$loadingText === void 0 ? 'Laster inn …' : _ref$loadingText,
    _ref$noMatchesText = _ref.noMatchesText,
    noMatchesText = _ref$noMatchesText === void 0 ? 'Ingen treff for søket' : _ref$noMatchesText,
    selectAllCheckboxState = _ref.selectAllCheckboxState,
    selectAllItem = _ref.selectAllItem,
    selectedItems = _ref.selectedItems,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$5);
  var isMultiselect = selectAllItem !== undefined;
  var isNoMatches = !loading && (listItems.length === 0 || (listItems == null ? void 0 : listItems.length) === 1 && (listItems == null ? void 0 : (_listItems$ = listItems[0]) == null ? void 0 : _listItems$.value) === (selectAllItem == null ? void 0 : selectAllItem.value));
  var isItemSelected = function isItemSelected(item) {
    return selectedItems.some(function (selectedItem) {
      return (selectedItem == null ? void 0 : selectedItem.value) === (item == null ? void 0 : item.value) && (selectedItem == null ? void 0 : selectedItem.label) === (item == null ? void 0 : item.label);
    });
  };
  var ariaValuesSelectAll = function ariaValuesSelectAll() {
    switch (selectAllCheckboxState == null ? void 0 : selectAllCheckboxState()) {
      case 'indeterminate':
        {
          return {
            label: (selectAllItem == null ? void 0 : selectAllItem.label) + ", delvis valgt",
            selected: false
          };
        }
      case true:
        {
          return {
            label: (selectAllItem == null ? void 0 : selectAllItem.label) + ", " + ariaLabelChosenSingular,
            selected: true
          };
        }
      default:
        {
          return {
            label: "" + (selectAllItem == null ? void 0 : selectAllItem.label),
            selected: false
          };
        }
    }
  };
  var selectAllListItemContent = function selectAllListItemContent() {
    return React.createElement(React.Fragment, null, React.createElement(Checkbox, {
      "aria-hidden": "true",
      checked: selectAllCheckboxState == null ? void 0 : selectAllCheckboxState(),
      className: "eds-dropdown__list__item__checkbox",
      onChange: function onChange() {
        return;
      },
      tabIndex: -1
    }), React.createElement("span", {
      className: "eds-dropdown__list__item__text",
      "aria-label": ariaValuesSelectAll().label
    }, selectAllItem == null ? void 0 : selectAllItem.label));
  };
  var listItemContent = function listItemContent(item) {
    return React.createElement(React.Fragment, null, React.createElement(Checkbox, {
      "aria-hidden": "true",
      checked: isItemSelected(item),
      className: "eds-dropdown__list__item__checkbox",
      onChange: function onChange() {
        return;
      },
      style: !isMultiselect ? {
        display: 'none'
      } : {},
      tabIndex: -1
    }), React.createElement("span", {
      className: "eds-dropdown__list__item__text"
    }, item.label, React.createElement(VisuallyHidden, null, isItemSelected(item) ? ariaLabelSelectedItem : '')), item.icons && React.createElement("span", null, item.icons.map(function (Icon) {
      var _Icon$displayName;
      return React.createElement(Icon, {
        key: (item == null ? void 0 : item.label) + (item == null ? void 0 : item.value) + ((_Icon$displayName = Icon == null ? void 0 : Icon.displayName) != null ? _Icon$displayName : Icon == null ? void 0 : Icon.name),
        inline: true,
        className: "eds-dropdown__list__item__icon"
      });
    })));
  };
  return (
    // use popover from @entur/tooltip when that package upgrades to floating-ui
    React.createElement("ul", _extends({}, getMenuProps({
      'aria-multiselectable': isMultiselect,
      ref: mergeRefs(getMenuProps().ref, listRef)
    }), {
      className: "eds-dropdown__list",
      style: _extends({
        display: isOpen ? 'inline-block' : 'none'
      }, rest.style, listStyle)
    }), !loading && listItems.length > 0 && listItems.map(function (item, index) {
      var itemIsSelectAll = item.value === (selectAllItem == null ? void 0 : selectAllItem.value);
      if (itemIsSelectAll && listItems.length <= 2) return null;
      return React.createElement("li", _extends({
        key: (item == null ? void 0 : item.label) + (item == null ? void 0 : item.value),
        className: classNames('eds-dropdown__list__item', {
          'eds-dropdown__list__item--select-all': itemIsSelectAll,
          'eds-dropdown__list__item--highlighted': highlightedIndex === index,
          'eds-dropdown__list__item--selected': !isMultiselect && isItemSelected(item)
        })
      }, getItemProps({
        key: (item == null ? void 0 : item.label) + (item == null ? void 0 : item.value),
        // @ts-expect-error Since getItemProps expects the same item type
        // here as items, it throws error when selectAllItem is a string.
        // This does, however, not cause any functional issues.
        item: item,
        index: index,
        'aria-selected': itemIsSelectAll ? ariaValuesSelectAll().selected : isItemSelected(item)
      })), itemIsSelectAll ? selectAllListItemContent() : listItemContent(item));
    }), isNoMatches && React.createElement("li", {
      key: "dropdown-list-no-match",
      className: "eds-dropdown__list__item"
    }, noMatchesText), loading && React.createElement("li", {
      key: "dropdown-list-loading",
      className: "eds-dropdown__list__item"
    }, loadingText))
  );
};
var _excluded$4 = ["tabIndex"];
var SelectedItemTag = function SelectedItemTag(_ref) {
  var _getSelectedItemProps;
  var ariaLabelRemoveSelected = _ref.ariaLabelRemoveSelected,
    _ref$ariaLabelChosen = _ref.ariaLabelChosen,
    ariaLabelChosen = _ref$ariaLabelChosen === void 0 ? 'valgt' : _ref$ariaLabelChosen,
    disabled = _ref.disabled,
    getSelectedItemProps = _ref.getSelectedItemProps,
    index = _ref.index,
    readOnly = _ref.readOnly,
    removeSelectedItem = _ref.removeSelectedItem,
    selectedItem = _ref.selectedItem;
  var _ref2 = (_getSelectedItemProps = getSelectedItemProps == null ? void 0 : getSelectedItemProps({
      selectedItem: selectedItem,
      index: index
    })) != null ? _getSelectedItemProps : {},
    selectedItemProps = _objectWithoutPropertiesLoose(_ref2, _excluded$4);
  return React.createElement(TagChip, _extends({
    size: "small",
    className: classNames('eds-dropdown__selected-item-tag', {
      'eds-dropdown__selected-item-tag--readonly': readOnly,
      'eds-dropdown__selected-item-tag--disabled': disabled
    })
  }, selectedItemProps, {
    onClose: function onClose(e) {
      e.stopPropagation();
      removeSelectedItem(selectedItem);
    },
    closeButtonAriaLabel: selectedItem.label + " " + ariaLabelChosen + ", " + ariaLabelRemoveSelected + " ",
    key: selectedItem.value,
    "aria-live": "polite"
  }), React.createElement("span", {
    "aria-hidden": "true"
  }, selectedItem.label));
};
var FieldAppend$1 = function FieldAppend(_ref3) {
  var _ref3$ariaHiddenToggl = _ref3.ariaHiddenToggleButton,
    ariaHiddenToggleButton = _ref3$ariaHiddenToggl === void 0 ? false : _ref3$ariaHiddenToggl,
    ariaLabelCloseList = _ref3.ariaLabelCloseList,
    ariaLabelOpenList = _ref3.ariaLabelOpenList,
    _ref3$clearable = _ref3.clearable,
    clearable = _ref3$clearable === void 0 ? false : _ref3$clearable,
    labelClearSelectedItems = _ref3.labelClearSelectedItems,
    _ref3$disabled = _ref3.disabled,
    disabled = _ref3$disabled === void 0 ? false : _ref3$disabled,
    _ref3$focusable = _ref3.focusable,
    focusable = _ref3$focusable === void 0 ? false : _ref3$focusable,
    getToggleButtonProps = _ref3.getToggleButtonProps,
    isOpen = _ref3.isOpen,
    _ref3$loading = _ref3.loading,
    loading = _ref3$loading === void 0 ? false : _ref3$loading,
    _ref3$loadingText = _ref3.loadingText,
    loadingText = _ref3$loadingText === void 0 ? 'Laster resultater …' : _ref3$loadingText,
    onClear = _ref3.onClear,
    selectedItems = _ref3.selectedItems;
  if (disabled) {
    return null;
  }
  return React.createElement("div", {
    className: "eds-dropdown__appendix"
  }, clearable && (selectedItems == null ? void 0 : selectedItems.length) > 0 && selectedItems[0] !== null && React.createElement(React.Fragment, null, React.createElement(ClearableButton, {
    onClear: onClear,
    focusable: true,
    labelClearSelectedItems: labelClearSelectedItems
  }), React.createElement("div", {
    className: "eds-dropdown__appendix__divider"
  })), !loading ? React.createElement(ToggleButton, {
    "aria-hidden": ariaHiddenToggleButton,
    ariaLabelCloseList: ariaLabelCloseList,
    ariaLabelOpenList: ariaLabelOpenList,
    getToggleButtonProps: getToggleButtonProps,
    isOpen: isOpen,
    focusable: focusable
  }) : React.createElement("div", {
    className: 'eds-dropdown__appendix__toggle-button--loading-dots'
  }, React.createElement(LoadingDots, {
    "aria-label": loadingText
  })));
};
var ClearableButton = function ClearableButton(_ref4) {
  var onClear = _ref4.onClear,
    _ref4$labelClearSelec = _ref4.labelClearSelectedItems,
    labelClearSelectedItems = _ref4$labelClearSelec === void 0 ? 'Fjern valgte' : _ref4$labelClearSelec,
    _ref4$focusable = _ref4.focusable,
    focusable = _ref4$focusable === void 0 ? false : _ref4$focusable;
  return React.createElement(Tooltip, {
    "aria-hidden": "true",
    placement: "top",
    content: labelClearSelectedItems,
    className: "eds-dropdown__appendix__clear-button__tooltip"
  }, React.createElement(IconButton, {
    className: "eds-dropdown__appendix__clear-button",
    type: "button",
    tabIndex: focusable ? 0 : 1,
    onClick: onClear,
    "aria-label": labelClearSelectedItems
  }, React.createElement(CloseSmallIcon, {
    "aria-hidden": "true"
  })));
};
var ToggleButton = function ToggleButton(_ref5) {
  var getToggleButtonProps = _ref5.getToggleButtonProps,
    isOpen = _ref5.isOpen,
    _ref5$ariaHidden = _ref5['aria-hidden'],
    ariaHidden = _ref5$ariaHidden === void 0 ? false : _ref5$ariaHidden,
    _ref5$ariaLabelCloseL = _ref5.ariaLabelCloseList,
    ariaLabelCloseList = _ref5$ariaLabelCloseL === void 0 ? 'Lukk liste med valg' : _ref5$ariaLabelCloseL,
    _ref5$ariaLabelOpenLi = _ref5.ariaLabelOpenList,
    ariaLabelOpenList = _ref5$ariaLabelOpenLi === void 0 ? 'Åpne liste med valg' : _ref5$ariaLabelOpenLi,
    _ref5$focusable = _ref5.focusable,
    focusable = _ref5$focusable === void 0 ? false : _ref5$focusable;
  return React.createElement(IconButton, _extends({}, getToggleButtonProps({
    className: classNames('eds-dropdown__appendix__toggle-button', {
      'eds-dropdown__appendix__toggle-button--open': isOpen
    }),
    'aria-labelledby': undefined
  }), {
    "aria-hidden": ariaHidden,
    "aria-label": ariaHidden ? undefined : isOpen ? ariaLabelCloseList : ariaLabelOpenList,
    tabIndex: focusable ? 0 : -1,
    type: "button"
  }), React.createElement(DownArrowIcon, {
    "aria-hidden": "true"
  }));
};

/* start general utils */
var EMPTY_INPUT = '';
function lowerCaseFilterTest(item, input) {
  if (!input) {
    return true;
  }
  var sanitizeEscapeCharacters = input.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  var inputRegex = new RegExp(sanitizeEscapeCharacters, 'i');
  return inputRegex.test(item.label);
}
function noFilter(
//@ts-expect-error only here to comply with dropdown filter API
item,
//@ts-expect-error only here to comply with dropdown filter API
input) {
  return true;
}
var itemToString = function itemToString(item) {
  return item ? item.label : '';
};
var isFunctionWithQueryArgument = function isFunctionWithQueryArgument(object) {
  return typeof object === 'function' && object.length > 0;
};
var useMultiselectUtils = function useMultiselectUtils(_ref) {
  var listItems = _ref.listItems,
    selectedItems = _ref.selectedItems,
    selectAll = _ref.selectAll;
  var hasSelectedItems = selectedItems.length > 0;
  var listItemsWithoutSelectAll = listItems.filter(function (item) {
    return item.value !== selectAll.value;
  });
  var unselectedItemsInListItems = listItemsWithoutSelectAll.filter(function (listItem) {
    return !selectedItems.some(function (selectedItem) {
      return selectedItem.value === listItem.value;
    });
  });
  var allListItemsAreSelected = !listItemsWithoutSelectAll.some(function (listItem) {
    return !selectedItems.some(function (selectedItem) {
      return selectedItem.value === listItem.value;
    });
  });
  var someListItemsAreSelected = listItemsWithoutSelectAll.some(function (listItem) {
    return selectedItems.some(function (selectedItem) {
      return selectedItem.value === listItem.value;
    });
  });
  var addClickedItemToSelectedItems = function addClickedItemToSelectedItems(clickedItem, onChange) {
    return onChange([].concat(selectedItems, [clickedItem]));
  };
  var clickedItemIsInSelectedItems = function clickedItemIsInSelectedItems(clickedItem) {
    return selectedItems.some(function (selectedItem) {
      return selectedItem.value === clickedItem.value;
    });
  };
  var clickedItemIsSelectAll = function clickedItemIsSelectAll(clickedItem) {
    return clickedItem.value === selectAll.value;
  };
  var handleListItemClicked = function handleListItemClicked(_ref2) {
    var clickedItem = _ref2.clickedItem,
      onChange = _ref2.onChange,
      setLastRemovedItem = _ref2.setLastRemovedItem;
    if (clickedItemIsSelectAll(clickedItem)) {
      if (allListItemsAreSelected) {
        setLastRemovedItem(selectAll);
        return unselectAllListItems(onChange);
      }
      return selectAllUnselectedItemsInListItems(onChange);
    }
    if (clickedItemIsInSelectedItems(clickedItem)) {
      setLastRemovedItem(clickedItem);
      return removeClickedItemFromSelectedItems(clickedItem, onChange);
    }
    addClickedItemToSelectedItems(clickedItem, onChange);
  };
  var removeClickedItemFromSelectedItems = function removeClickedItemFromSelectedItems(clickedItem, onChange) {
    return onChange(selectedItems.filter(function (selectedItem) {
      return selectedItem.value !== clickedItem.value;
    }));
  };
  var selectAllCheckboxState = function selectAllCheckboxState() {
    if (allListItemsAreSelected) return true;
    if (someListItemsAreSelected) return 'indeterminate';
    return false;
  };
  var selectAllUnselectedItemsInListItems = function selectAllUnselectedItemsInListItems(onChange) {
    onChange([].concat(selectedItems, unselectedItemsInListItems));
  };
  var unselectAllListItems = function unselectAllListItems(onChange) {
    var selectedItemsWithoutItemsInListItems = selectedItems.filter(function (selectedItem) {
      return !listItemsWithoutSelectAll.some(function (listItem) {
        return listItem.value === selectedItem.value;
      });
    });
    onChange(selectedItemsWithoutItemsInListItems);
  };
  return {
    addClickedItemToSelectedItems: addClickedItemToSelectedItems,
    allListItemsAreSelected: allListItemsAreSelected,
    clickedItemIsInSelectedItems: clickedItemIsInSelectedItems,
    clickedItemIsSelectAll: clickedItemIsSelectAll,
    handleListItemClicked: handleListItemClicked,
    hasSelectedItems: hasSelectedItems,
    listItemsWithoutSelectAll: listItemsWithoutSelectAll,
    removeClickedItemFromSelectedItems: removeClickedItemFromSelectedItems,
    selectAllCheckboxState: selectAllCheckboxState,
    selectAllUnselectedItemsInListItems: selectAllUnselectedItemsInListItems,
    someListItemsAreSelected: someListItemsAreSelected,
    unselectAllListItems: unselectAllListItems
  };
};
function getA11yStatusMessage(options) {
  var isOpen = options.isOpen,
    resultCount = options.resultCount,
    previousResultCount = options.previousResultCount,
    _options$selectAllIte = options.selectAllItemIncluded,
    selectAllItemIncluded = _options$selectAllIte === void 0 ? false : _options$selectAllIte,
    _options$ariaLabelNoR = options.ariaLabelNoResults,
    ariaLabelNoResults = _options$ariaLabelNoR === void 0 ? 'Ingen resultater' : _options$ariaLabelNoR;
  if (!isOpen) {
    return '';
  }
  var resultCountWithoutSelectAll = selectAllItemIncluded ? resultCount - 1 : resultCount;
  if (resultCountWithoutSelectAll === 0) {
    return ariaLabelNoResults;
  }
  if (resultCount !== previousResultCount) {
    return resultCountWithoutSelectAll + " resultat" + (resultCountWithoutSelectAll === 1 ? '' : 'er') + " tilgjengelig, naviger med pil opp eller ned, velg elementer med enter.";
  }
  return '';
}
function getA11ySelectionMessage(options) {
  var selectedItem = options.selectedItem,
    itemToStringLocal = options.itemToString,
    selectAllItem = options.selectAllItem;
  if ((selectedItem == null ? void 0 : selectedItem.value) === (selectAllItem == null ? void 0 : selectAllItem.value)) return 'Alle elementer i listen valgt.';
  return selectedItem ? itemToStringLocal(selectedItem) + " er valgt." : '';
}
function getA11yRemovalMessage(options) {
  var itemToString = options.itemToString,
    selectAllItem = options.selectAllItem,
    removedItem = options.removedItem;
  if (removedItem === undefined) return '';
  if (removedItem.value === (selectAllItem == null ? void 0 : selectAllItem.value)) return 'Alle elementer i listen fjernet fra valgte.';
  return itemToString(removedItem) + " fjernet fra valgte.";
}
/* end a11y utils */

var _excluded$3 = ["ariaLabelChosenSingular", "ariaLabelCloseList", "ariaLabelOpenList", "ariaLabelSelectedItem", "className", "clearable", "debounceTimeout", "disabled", "disableLabelAnimation", "feedback", "items", "itemFilter", "label", "labelClearSelectedItem", "labelTooltip", "listStyle", "loadingText", "noMatchesText", "onChange", "placeholder", "prepend", "readOnly", "selectedItem", "selectOnBlur", "selectOnTab", "style", "variant"];
var SearchableDropdown = function SearchableDropdown(_ref) {
  var _document, _selectedItem$label;
  var ariaLabelChosenSingular = _ref.ariaLabelChosenSingular,
    ariaLabelCloseList = _ref.ariaLabelCloseList,
    ariaLabelOpenList = _ref.ariaLabelOpenList,
    ariaLabelSelectedItem = _ref.ariaLabelSelectedItem,
    className = _ref.className,
    _ref$clearable = _ref.clearable,
    clearable = _ref$clearable === void 0 ? true : _ref$clearable,
    debounceTimeout = _ref.debounceTimeout,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$disableLabelAnim = _ref.disableLabelAnimation,
    disableLabelAnimation = _ref$disableLabelAnim === void 0 ? false : _ref$disableLabelAnim,
    feedback = _ref.feedback,
    initialItems = _ref.items,
    _ref$itemFilter = _ref.itemFilter,
    itemFilter = _ref$itemFilter === void 0 ? isFunctionWithQueryArgument(initialItems) ? noFilter : lowerCaseFilterTest : _ref$itemFilter,
    label = _ref.label,
    _ref$labelClearSelect = _ref.labelClearSelectedItem,
    labelClearSelectedItem = _ref$labelClearSelect === void 0 ? 'fjern valgt' : _ref$labelClearSelect,
    labelTooltip = _ref.labelTooltip,
    listStyle = _ref.listStyle,
    loadingText = _ref.loadingText,
    noMatchesText = _ref.noMatchesText,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? function () {
      return undefined;
    } : _ref$onChange,
    placeholder = _ref.placeholder,
    prepend = _ref.prepend,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    value = _ref.selectedItem,
    _ref$selectOnBlur = _ref.selectOnBlur,
    selectOnBlur = _ref$selectOnBlur === void 0 ? false : _ref$selectOnBlur,
    _ref$selectOnTab = _ref.selectOnTab,
    selectOnTab = _ref$selectOnTab === void 0 ? false : _ref$selectOnTab,
    style = _ref.style,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'info' : _ref$variant,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$3);
  var _useState = useState(value !== null),
    showSelectedItem = _useState[0],
    setShowSelectedItem = _useState[1];
  var _useState2 = useState(0),
    lastHighlightedIndex = _useState2[0],
    setLastHighlightedIndex = _useState2[1];
  var inputRef = useRef(null);
  var _useResolvedItems = useResolvedItems(initialItems, debounceTimeout),
    normalizedItems = _useResolvedItems.items,
    loading = _useResolvedItems.loading,
    fetchItems = _useResolvedItems.fetchItems;
  var _useState3 = useState(normalizedItems),
    listItems = _useState3[0],
    setListItems = _useState3[1];
  var filterListItems = function filterListItems(_ref2) {
    var inputValue = _ref2.inputValue;
    return setListItems(normalizedItems.filter(function (item) {
      return itemFilter(item, inputValue);
    }));
  };
  var updateListItems = function updateListItems(_ref3) {
    var inputValue = _ref3.inputValue;
    var shouldRefetchItems = isFunctionWithQueryArgument(initialItems);
    if (shouldRefetchItems) fetchItems(inputValue != null ? inputValue : EMPTY_INPUT);
    filterListItems({
      inputValue: inputValue != null ? inputValue : EMPTY_INPUT
    });
  };
  var inputHasFocus = typeof document !== 'undefined' ? (inputRef == null ? void 0 : inputRef.current) === ((_document = document) == null ? void 0 : _document.activeElement) : false;
  useEffect(function () {
    filterListItems({
      inputValue: inputValue
    });
  }, [normalizedItems]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(function () {
    // sync internal state on initial render
    if (selectedItem !== null && !inputHasFocus) {
      setShowSelectedItem(true);
      updateListItems({
        inputValue: EMPTY_INPUT
      });
      setInputValue(EMPTY_INPUT);
    }
  }, []);
  var stateReducer = useCallback(function (_, _ref4) {
    var type = _ref4.type,
      changes = _ref4.changes;
    if (changes.highlightedIndex !== undefined && (changes == null ? void 0 : changes.highlightedIndex) >= 0) {
      setLastHighlightedIndex(changes == null ? void 0 : changes.highlightedIndex);
    }
    switch (type) {
      // empty input to show selected item and reset dropdown list on item selection
      case useCombobox.stateChangeTypes.ItemClick:
      case useCombobox.stateChangeTypes.InputKeyDownEnter:
      case useCombobox.stateChangeTypes.InputBlur:
        {
          updateListItems({
            inputValue: EMPTY_INPUT
          });
          return _extends({}, changes, {
            inputValue: EMPTY_INPUT
          });
        }
      case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
        if (changes.selectedItem !== null && !inputHasFocus) setShowSelectedItem(true);
        updateListItems({
          inputValue: EMPTY_INPUT
        });
        return _extends({}, changes, {
          inputValue: EMPTY_INPUT
        });
      // remove leading whitespace, select element with spacebar on empty input, and filter list based on input
      case useCombobox.stateChangeTypes.InputChange:
        {
          var _changes$inputValue;
          var leadingWhitespaceTest = /^\s+/g;
          var isSpacePressedOnEmptyInput = changes.inputValue === ' ';
          if ((_changes$inputValue = changes.inputValue) != null && _changes$inputValue.match(leadingWhitespaceTest)) {
            setInputValue(changes.inputValue.replace(leadingWhitespaceTest, EMPTY_INPUT));
            if (isSpacePressedOnEmptyInput) {
              openMenu();
              if (isOpen && changes.highlightedIndex !== undefined) {
                onChange(listItems[changes.highlightedIndex]);
              }
            }
          } else {
            updateListItems({
              inputValue: changes.inputValue
            });
            setHighlightedIndex(0);
            setLastHighlightedIndex(0);
          }
          return changes;
        }
      default:
        return changes;
    }
  }, [fetchItems, filterListItems]);
  var _useCombobox = useCombobox(_extends({
      defaultHighlightedIndex: lastHighlightedIndex,
      items: listItems,
      itemToString: itemToString,
      selectedItem: value,
      stateReducer: stateReducer,
      onStateChange: function onStateChange(_ref5) {
        var type = _ref5.type,
          newSelectedItem = _ref5.selectedItem;
        switch (type) {
          // @ts-expect-error This falltrough is wanted
          case useCombobox.stateChangeTypes.InputBlur:
            if (!selectOnBlur) break;
          case useCombobox.stateChangeTypes.InputKeyDownEnter: // eslint-disable-line no-fallthrough
          case useCombobox.stateChangeTypes.ItemClick:
            if (newSelectedItem === undefined) return;
            onChange(newSelectedItem != null ? newSelectedItem : null);
        }
      },
      // Accessibility
      getA11yStatusMessage: getA11yStatusMessage,
      // The following A11y-helper does not work due to a bug (https://github.com/downshift-js/downshift/issues/1227)
      // but is left here for when it is fixed
      getA11ySelectionMessage: function getA11ySelectionMessage$1(options) {
        return getA11ySelectionMessage(options);
      }
    }, rest)),
    isOpen = _useCombobox.isOpen,
    openMenu = _useCombobox.openMenu,
    getToggleButtonProps = _useCombobox.getToggleButtonProps,
    getLabelProps = _useCombobox.getLabelProps,
    getMenuProps = _useCombobox.getMenuProps,
    getInputProps = _useCombobox.getInputProps,
    highlightedIndex = _useCombobox.highlightedIndex,
    setHighlightedIndex = _useCombobox.setHighlightedIndex,
    getItemProps = _useCombobox.getItemProps,
    selectedItem = _useCombobox.selectedItem,
    inputValue = _useCombobox.inputValue,
    setInputValue = _useCombobox.setInputValue;
  var _useFloating = useFloating({
      whileElementsMounted: function whileElementsMounted(ref, _float, update) {
        return autoUpdate(ref, _float, update);
      },
      placement: 'bottom-start',
      open: isOpen,
      middleware: [offset(space.extraSmall2), flip()]
    }),
    refs = _useFloating.refs,
    floatingStyles = _useFloating.floatingStyles;
  var handleOnClear = function handleOnClear() {
    var _inputRef$current;
    onChange(null);
    setInputValue(EMPTY_INPUT);
    (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.focus();
    updateListItems({
      inputValue: inputValue
    });
    setShowSelectedItem(false);
  };
  return React.createElement("div", {
    className: classNames('eds-dropdown__wrapper', className, {
      'eds-dropdown__wrapper--has-tooltip': labelTooltip !== undefined
    }),
    style: style
  }, React.createElement(BaseFormControl, _extends({
    className: classNames('eds-dropdown', 'eds-dropdown--searchable'),
    disabled: disabled,
    disableLabelAnimation: disableLabelAnimation,
    feedback: feedback,
    isFilled: selectedItem !== null || inputValue !== EMPTY_INPUT,
    label: label,
    labelId: getLabelProps().id,
    labelProps: getLabelProps(),
    labelTooltip: labelTooltip,
    onClick: function onClick(e) {
      var _inputRef$current2;
      if (e.target === e.currentTarget) (_inputRef$current2 = inputRef.current) == null ? void 0 : _inputRef$current2.focus();
    },
    prepend: prepend,
    readOnly: readOnly,
    ref: refs.setReference,
    variant: variant
  }, rest, {
    append: React.createElement(FieldAppend$1, {
      ariaLabelCloseList: ariaLabelCloseList,
      ariaLabelOpenList: ariaLabelOpenList,
      clearable: clearable,
      labelClearSelectedItems: labelClearSelectedItem,
      disabled: readOnly || disabled,
      focusable: false,
      getToggleButtonProps: getToggleButtonProps,
      isOpen: isOpen,
      loading: loading,
      loadingText: loadingText,
      onClear: handleOnClear,
      selectedItems: [selectedItem]
    })
  }), React.createElement("span", {
    className: classNames('eds-dropdown--searchable__selected-item', {
      'eds-dropdown--searchable__selected-item--hidden': !showSelectedItem
    }),
    "aria-hidden": "true",
    onClick: function onClick() {
      var _inputRef$current3;
      (_inputRef$current3 = inputRef.current) == null ? void 0 : _inputRef$current3.focus();
      openMenu();
    }
  }, showSelectedItem ? selectedItem == null ? void 0 : selectedItem.label : ''), React.createElement("input", _extends({
    className: classNames('eds-dropdown__input eds-form-control', {
      'eds-dropdown__input--hidden': showSelectedItem
    }),
    disabled: readOnly || disabled,
    placeholder: (_selectedItem$label = selectedItem == null ? void 0 : selectedItem.label) != null ? _selectedItem$label : placeholder
  }, getInputProps({
    onBlur: function onBlur() {
      if (selectedItem !== null) setShowSelectedItem(true);
    },
    onFocus: function onFocus() {
      setShowSelectedItem(false);
    },
    onKeyDown: function onKeyDown(e) {
      if (selectOnTab && isOpen && e.key === 'Tab') onChange == null ? void 0 : onChange(listItems[highlightedIndex]);
    },
    ref: inputRef
  })))), React.createElement(DropdownList, {
    ariaLabelChosenSingular: ariaLabelChosenSingular,
    ariaLabelSelectedItem: ariaLabelSelectedItem,
    isOpen: isOpen,
    listItems: listItems,
    listStyle: _extends({}, floatingStyles, listStyle),
    listRef: refs.setFloating,
    loading: loading,
    loadingText: loadingText,
    noMatchesText: noMatchesText,
    getItemProps: getItemProps,
    getMenuProps: getMenuProps,
    highlightedIndex: highlightedIndex,
    selectedItems: selectedItem !== null ? [selectedItem] : []
  }));
};
var _excluded$2 = ["className", "clearable", "clearInputOnSelect", "debounceTimeout", "disabled", "feedback", "hideSelectAll", "items", "itemFilter", "label", "labelAllItemsSelected", "labelClearAllItems", "labelSelectAll", "labelTooltip", "listStyle", "loadingText", "maxChips", "noMatchesText", "onChange", "placeholder", "readOnly", "selectedItems", "selectOnBlur", "selectOnTab", "style", "variant", "ariaLabelChosenSingular", "ariaLabelChosenPlural", "ariaLabelCloseList", "ariaLabelJumpToInput", "ariaLabelOpenList", "ariaLabelRemoveSelected", "ariaLabelSelectedItem"];
var MultiSelect = function MultiSelect(_ref) {
  var className = _ref.className,
    _ref$clearable = _ref.clearable,
    clearable = _ref$clearable === void 0 ? true : _ref$clearable,
    _ref$clearInputOnSele = _ref.clearInputOnSelect,
    clearInputOnSelect = _ref$clearInputOnSele === void 0 ? false : _ref$clearInputOnSele,
    debounceTimeout = _ref.debounceTimeout,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    feedback = _ref.feedback,
    _ref$hideSelectAll = _ref.hideSelectAll,
    hideSelectAll = _ref$hideSelectAll === void 0 ? false : _ref$hideSelectAll,
    initialItems = _ref.items,
    _ref$itemFilter = _ref.itemFilter,
    itemFilter = _ref$itemFilter === void 0 ? isFunctionWithQueryArgument(initialItems) ? noFilter : lowerCaseFilterTest : _ref$itemFilter,
    label = _ref.label,
    _ref$labelAllItemsSel = _ref.labelAllItemsSelected,
    labelAllItemsSelected = _ref$labelAllItemsSel === void 0 ? 'Alle valgt' : _ref$labelAllItemsSel,
    _ref$labelClearAllIte = _ref.labelClearAllItems,
    labelClearAllItems = _ref$labelClearAllIte === void 0 ? 'Fjern valgte' : _ref$labelClearAllIte,
    _ref$labelSelectAll = _ref.labelSelectAll,
    labelSelectAll = _ref$labelSelectAll === void 0 ? 'Velg alle' : _ref$labelSelectAll,
    labelTooltip = _ref.labelTooltip,
    listStyle = _ref.listStyle,
    loadingText = _ref.loadingText,
    _ref$maxChips = _ref.maxChips,
    maxChips = _ref$maxChips === void 0 ? 10 : _ref$maxChips,
    noMatchesText = _ref.noMatchesText,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? function () {
      return undefined;
    } : _ref$onChange,
    placeholder = _ref.placeholder,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    _ref$selectedItems = _ref.selectedItems,
    selectedItems = _ref$selectedItems === void 0 ? [] : _ref$selectedItems,
    _ref$selectOnBlur = _ref.selectOnBlur,
    selectOnBlur = _ref$selectOnBlur === void 0 ? false : _ref$selectOnBlur,
    _ref$selectOnTab = _ref.selectOnTab,
    selectOnTab = _ref$selectOnTab === void 0 ? false : _ref$selectOnTab,
    style = _ref.style,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'information' : _ref$variant,
    ariaLabelChosenSingular = _ref.ariaLabelChosenSingular,
    _ref$ariaLabelChosenP = _ref.ariaLabelChosenPlural,
    ariaLabelChosenPlural = _ref$ariaLabelChosenP === void 0 ? 'valgte' : _ref$ariaLabelChosenP,
    ariaLabelCloseList = _ref.ariaLabelCloseList,
    _ref$ariaLabelJumpToI = _ref.ariaLabelJumpToInput,
    ariaLabelJumpToInput = _ref$ariaLabelJumpToI === void 0 ? selectedItems.length + " valgte elementer, trykk for \xE5 hoppe til tekstfeltet" : _ref$ariaLabelJumpToI,
    ariaLabelOpenList = _ref.ariaLabelOpenList,
    _ref$ariaLabelRemoveS = _ref.ariaLabelRemoveSelected,
    ariaLabelRemoveSelected = _ref$ariaLabelRemoveS === void 0 ? 'trykk for å fjerne valg' : _ref$ariaLabelRemoveS,
    ariaLabelSelectedItem = _ref.ariaLabelSelectedItem,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  var _React$useState = React.useState(0),
    lastHighlightedIndex = _React$useState[0],
    setLastHighlightedIndex = _React$useState[1];
  var _React$useState2 = React.useState(undefined),
    lastRemovedItem = _React$useState2[0],
    setLastRemovedItem = _React$useState2[1];
  var inputRef = useRef(null);
  useEffect(function () {
    //@ts-expect-error this is done to aid developers debug wrong prop usage
    if (rest.selectedItem !== undefined) console.warn("Incorrect 'selectedItem' prop found, did you mean to use 'selectedItems?");
  }, []);
  var _useResolvedItems = useResolvedItems(initialItems, debounceTimeout),
    normalizedItems = _useResolvedItems.items,
    loading = _useResolvedItems.loading,
    fetchItems = _useResolvedItems.fetchItems;
  var isAllNonAsyncItemsSelected = typeof initialItems !== 'function' && selectedItems.length === normalizedItems.length;
  // special 'item' used as Select All entry in the dropdown list
  var selectAll = {
    value: useRandomId('select-all'),
    label: labelSelectAll
  };
  // special 'item' used as a replacement selected item tag for when
  // there are more selected element than maxChips
  var summarySelectedItems = React.useMemo(function () {
    return {
      value: EMPTY_INPUT,
      label: isAllNonAsyncItemsSelected ? labelAllItemsSelected : selectedItems.length + ' ' + ariaLabelChosenPlural
    };
  }, [isAllNonAsyncItemsSelected, selectedItems, labelAllItemsSelected, ariaLabelChosenPlural]);
  var _useState = useState([].concat(!hideSelectAll ? [selectAll] : [], normalizedItems)),
    listItems = _useState[0],
    setListItems = _useState[1];
  var filterListItems = function filterListItems(_ref2) {
    var inputValue = _ref2.inputValue;
    return setListItems([].concat(!hideSelectAll ? [selectAll] : [], normalizedItems.filter(function (item) {
      return itemFilter(item, inputValue);
    })));
  };
  var updateListItems = function updateListItems(_ref3) {
    var inputValue = _ref3.inputValue;
    var shouldRefetchItems = isFunctionWithQueryArgument(initialItems);
    if (shouldRefetchItems) fetchItems(inputValue != null ? inputValue : EMPTY_INPUT);
    filterListItems({
      inputValue: inputValue != null ? inputValue : EMPTY_INPUT
    });
  };
  React.useEffect(function () {
    filterListItems({
      inputValue: inputValue
    });
  }, [normalizedItems]); // eslint-disable-line react-hooks/exhaustive-deps
  var _useMultiselectUtils = useMultiselectUtils({
      listItems: listItems,
      selectAll: selectAll,
      selectedItems: selectedItems
    }),
    hasSelectedItems = _useMultiselectUtils.hasSelectedItems,
    handleListItemClicked = _useMultiselectUtils.handleListItemClicked,
    selectAllCheckboxState = _useMultiselectUtils.selectAllCheckboxState;
  var _useMultipleSelection = useMultipleSelection({
      selectedItems: selectedItems,
      itemToString: itemToString,
      onStateChange: function onStateChange(_ref4) {
        var newSelectedItems = _ref4.selectedItems,
          type = _ref4.type;
        switch (type) {
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            {
              if (newSelectedItems !== undefined) onChange(newSelectedItems);
              break;
            }
        }
      },
      // Accessibility
      getA11yRemovalMessage: function getA11yRemovalMessage$1(options) {
        return getA11yRemovalMessage(_extends({}, options, {
          selectAllItem: selectAll,
          removedItem: lastRemovedItem
        }));
      }
    }),
    getSelectedItemProps = _useMultipleSelection.getSelectedItemProps,
    getDropdownProps = _useMultipleSelection.getDropdownProps;
  var stateReducer = React.useCallback(function (_, _ref5) {
    var changes = _ref5.changes,
      type = _ref5.type;
    if (changes.highlightedIndex !== undefined && (changes == null ? void 0 : changes.highlightedIndex) >= 0) {
      setLastHighlightedIndex(changes == null ? void 0 : changes.highlightedIndex);
    }
    switch (type) {
      // keep menu open and edit input value on item selection
      case useCombobox.stateChangeTypes.InputKeyDownEnter:
      case useCombobox.stateChangeTypes.ItemClick:
        {
          var _inputRef$current$val, _inputRef$current;
          if (clearInputOnSelect) {
            updateListItems({
              inputValue: EMPTY_INPUT
            });
          }
          return _extends({}, changes, {
            isOpen: true,
            inputValue: clearInputOnSelect ? EMPTY_INPUT : (_inputRef$current$val = inputRef == null ? void 0 : (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.value) != null ? _inputRef$current$val : EMPTY_INPUT
          });
        }
      // edit input value when selected items is updated outside component
      case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
        {
          var _inputRef$current$val2, _inputRef$current2;
          return _extends({}, changes, {
            inputValue: (_inputRef$current$val2 = inputRef == null ? void 0 : (_inputRef$current2 = inputRef.current) == null ? void 0 : _inputRef$current2.value) != null ? _inputRef$current$val2 : EMPTY_INPUT
          });
        }
      // remove leading whitespace, select item with spacebar if input is empty and filter list items
      case useCombobox.stateChangeTypes.InputChange:
        {
          var _changes$inputValue;
          var leadingWhitespaceTest = /^\s+/g;
          var isSpacePressedOnEmptyInput = changes.inputValue === ' ';
          if ((_changes$inputValue = changes.inputValue) != null && _changes$inputValue.match(leadingWhitespaceTest)) {
            setInputValue(changes.inputValue.replace(leadingWhitespaceTest, EMPTY_INPUT));
            if (isSpacePressedOnEmptyInput) {
              openMenu();
              if (isOpen && changes.highlightedIndex !== undefined) {
                handleListItemClicked({
                  clickedItem: listItems[changes.highlightedIndex],
                  onChange: onChange,
                  setLastRemovedItem: setLastRemovedItem
                });
              }
            }
          } else {
            updateListItems({
              inputValue: changes.inputValue
            });
            // set highlighted item to first item after search
            setLastHighlightedIndex(hideSelectAll ? 0 : 1);
            return _extends({}, changes, {
              highlightedIndex: hideSelectAll ? 0 : 1
            });
          }
          return changes;
        }
      // reset input value when leaving input field
      case useCombobox.stateChangeTypes.InputBlur:
        {
          updateListItems({
            inputValue: EMPTY_INPUT
          });
          return _extends({}, changes, {
            inputValue: EMPTY_INPUT
          });
        }
      default:
        return changes;
    }
  }, [hideSelectAll, normalizedItems, filterListItems, initialItems]);
  var _useCombobox = useCombobox(_extends({
      defaultHighlightedIndex: lastHighlightedIndex,
      items: listItems,
      itemToString: itemToString,
      selectedItem: null,
      stateReducer: stateReducer,
      onStateChange: function onStateChange(_ref6) {
        var type = _ref6.type,
          clickedItem = _ref6.selectedItem;
        // clickedItem means item chosen either via mouse or keyboard
        if (!clickedItem) return;
        switch (type) {
          // @ts-expect-error This falltrough is wanted
          case useCombobox.stateChangeTypes.InputBlur:
            if (!selectOnBlur) break;
          case useCombobox.stateChangeTypes.InputKeyDownEnter: // eslint-disable-line no-fallthrough
          case useCombobox.stateChangeTypes.ItemClick:
            {
              handleListItemClicked({
                clickedItem: clickedItem,
                onChange: onChange,
                setLastRemovedItem: setLastRemovedItem
              });
            }
        }
      },
      // Accessibility
      getA11yStatusMessage: function getA11yStatusMessage$1(options) {
        return getA11yStatusMessage(_extends({}, options, {
          selectAllItemIncluded: !hideSelectAll
        }));
      },
      // The following A11y-helper does not work due to a bug (https://github.com/downshift-js/downshift/issues/1227)
      // but is left here for when it is fixed
      getA11ySelectionMessage: function getA11ySelectionMessage$1(options) {
        return getA11ySelectionMessage(_extends({}, options, {
          selectAllItem: selectAll
        }));
      }
    }, rest)),
    getInputProps = _useCombobox.getInputProps,
    getItemProps = _useCombobox.getItemProps,
    getLabelProps = _useCombobox.getLabelProps,
    getMenuProps = _useCombobox.getMenuProps,
    getToggleButtonProps = _useCombobox.getToggleButtonProps,
    highlightedIndex = _useCombobox.highlightedIndex,
    inputValue = _useCombobox.inputValue,
    isOpen = _useCombobox.isOpen,
    openMenu = _useCombobox.openMenu,
    setInputValue = _useCombobox.setInputValue;
  var _useFloating = useFloating({
      whileElementsMounted: function whileElementsMounted(ref, _float, update) {
        return autoUpdate(ref, _float, update);
      },
      placement: 'bottom-start',
      open: isOpen,
      middleware: [offset(space.extraSmall2), flip()]
    }),
    refs = _useFloating.refs,
    floatingStyles = _useFloating.floatingStyles;
  var handleOnClear = function handleOnClear() {
    var _inputRef$current3;
    onChange([]);
    setInputValue(EMPTY_INPUT);
    (_inputRef$current3 = inputRef.current) == null ? void 0 : _inputRef$current3.focus();
    updateListItems({
      inputValue: inputValue
    });
  };
  return React.createElement("div", {
    className: classNames('eds-dropdown__wrapper', className, {
      'eds-dropdown__wrapper--has-tooltip': labelTooltip !== undefined
    }),
    style: style
  }, React.createElement(BaseFormControl, _extends({
    append: React.createElement(FieldAppend$1, {
      ariaLabelCloseList: ariaLabelCloseList,
      ariaLabelOpenList: ariaLabelOpenList,
      selectedItems: selectedItems,
      isOpen: isOpen,
      clearable: clearable,
      labelClearSelectedItems: labelClearAllItems,
      focusable: false,
      loading: loading,
      loadingText: loadingText,
      disabled: readOnly || disabled,
      onClear: handleOnClear,
      getToggleButtonProps: getToggleButtonProps
    }),
    className: classNames('eds-dropdown', 'eds-dropdown--multiselect'),
    disabled: disabled,
    feedback: feedback,
    isFilled: hasSelectedItems || inputValue !== EMPTY_INPUT,
    label: label,
    labelId: getLabelProps().id,
    labelProps: getLabelProps(),
    labelTooltip: labelTooltip,
    onClick: function onClick(e) {
      var _inputRef$current4;
      if (e.target === e.currentTarget) (_inputRef$current4 = inputRef.current) == null ? void 0 : _inputRef$current4.focus();
    },
    readOnly: readOnly,
    ref: refs.setReference,
    variant: variant
  }, rest), React.createElement("div", {
    className: classNames('eds-dropdown--multiselect__selected-items-and-input', {
      'eds-dropdown--multiselect__selected-items-and-input--filled': hasSelectedItems
    }),
    onClick: function onClick(e) {
      if (e.target === e.currentTarget) openMenu();
    }
  }, selectedItems.length <= maxChips ? React.createElement(React.Fragment, null, selectedItems.length > 1 ? React.createElement(VisuallyHidden, {
    onClick: function onClick() {
      var _inputRef$current5;
      return (_inputRef$current5 = inputRef.current) == null ? void 0 : _inputRef$current5.focus();
    }
  }, ariaLabelJumpToInput) : React.createElement(React.Fragment, null), selectedItems.map(function (selectedItem, index) {
    return React.createElement(SelectedItemTag, {
      ariaLabelChosen: ariaLabelChosenSingular,
      ariaLabelRemoveSelected: ariaLabelRemoveSelected,
      disabled: disabled,
      getSelectedItemProps: getSelectedItemProps,
      index: index,
      key: (selectedItem == null ? void 0 : selectedItem.label) + (typeof (selectedItem == null ? void 0 : selectedItem.value) === 'string' ? selectedItem.value : ''),
      readOnly: readOnly,
      removeSelectedItem: function removeSelectedItem() {
        var _inputRef$current6;
        handleListItemClicked({
          clickedItem: selectedItem,
          onChange: onChange,
          setLastRemovedItem: setLastRemovedItem
        });
        inputRef == null ? void 0 : (_inputRef$current6 = inputRef.current) == null ? void 0 : _inputRef$current6.focus();
      },
      selectedItem: selectedItem
    });
  })) : React.createElement(SelectedItemTag, {
    ariaLabelRemoveSelected: labelClearAllItems,
    ariaLabelChosen: "",
    disabled: disabled,
    readOnly: readOnly,
    removeSelectedItem: handleOnClear,
    selectedItem: summarySelectedItems
  }), React.createElement("input", _extends({
    placeholder: placeholder,
    className: "eds-dropdown__input eds-form-control",
    disabled: readOnly || disabled
  }, getInputProps(_extends({
    onKeyDown: function onKeyDown(e) {
      if (selectOnTab && isOpen && e.key === 'Tab') {
        var highlitedItem = listItems[highlightedIndex];
        // we don't want to clear selection with tab
        if (highlitedItem) {
          handleListItemClicked({
            clickedItem: highlitedItem,
            onChange: onChange,
            setLastRemovedItem: setLastRemovedItem
          });
        }
      }
    }
  }, getDropdownProps({
    preventKeyAction: isOpen,
    ref: inputRef,
    value: inputValue != null ? inputValue : EMPTY_INPUT
  }))))))), React.createElement(DropdownList, {
    ariaLabelChosenSingular: ariaLabelChosenSingular,
    ariaLabelSelectedItem: ariaLabelSelectedItem,
    getItemProps: getItemProps,
    getMenuProps: getMenuProps,
    highlightedIndex: highlightedIndex,
    inputValue: inputValue,
    isOpen: isOpen,
    listItems: listItems,
    listStyle: _extends({}, floatingStyles, listStyle),
    listRef: refs.setFloating,
    loading: loading,
    loadingText: loadingText,
    noMatchesText: noMatchesText,
    selectAllCheckboxState: selectAllCheckboxState,
    selectAllItem: selectAll,
    selectedItems: selectedItems
  }));
};
var _excluded$1 = ["ariaLabelChosenSingular", "ariaLabelCloseList", "ariaLabelOpenList", "ariaLabelSelectedItem", "className", "clearable", "disabled", "disableLabelAnimation", "feedback", "items", "label", "labelClearSelectedItem", "labelTooltip", "listStyle", "loadingText", "onChange", "placeholder", "prepend", "readOnly", "selectedItem", "selectOnBlur", "selectOnTab", "style", "variant"];
var Dropdown = function Dropdown(_ref) {
  var _ref3, _selectedItem$label;
  var ariaLabelChosenSingular = _ref.ariaLabelChosenSingular,
    ariaLabelCloseList = _ref.ariaLabelCloseList,
    ariaLabelOpenList = _ref.ariaLabelOpenList,
    ariaLabelSelectedItem = _ref.ariaLabelSelectedItem,
    className = _ref.className,
    _ref$clearable = _ref.clearable,
    clearable = _ref$clearable === void 0 ? false : _ref$clearable,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    disableLabelAnimation = _ref.disableLabelAnimation,
    feedback = _ref.feedback,
    initialItems = _ref.items,
    label = _ref.label,
    _ref$labelClearSelect = _ref.labelClearSelectedItem,
    labelClearSelectedItem = _ref$labelClearSelect === void 0 ? 'fjern valgt' : _ref$labelClearSelect,
    labelTooltip = _ref.labelTooltip,
    listStyle = _ref.listStyle,
    loadingText = _ref.loadingText,
    onChange = _ref.onChange,
    placeholder = _ref.placeholder,
    prepend = _ref.prepend,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    selectedItem = _ref.selectedItem,
    _ref$selectOnBlur = _ref.selectOnBlur,
    selectOnBlur = _ref$selectOnBlur === void 0 ? false : _ref$selectOnBlur,
    _ref$selectOnTab = _ref.selectOnTab,
    selectOnTab = _ref$selectOnTab === void 0 ? false : _ref$selectOnTab,
    style = _ref.style,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'information' : _ref$variant,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  var _useResolvedItems = useResolvedItems(initialItems),
    normalizedItems = _useResolvedItems.items,
    loading = _useResolvedItems.loading;
  var toggleButtonRef = useRef(null);
  var isFilled = selectedItem !== null || placeholder !== undefined;
  var _useSelect = useSelect({
      items: normalizedItems,
      defaultHighlightedIndex: selectedItem ? undefined : 0,
      selectedItem: selectedItem,
      onStateChange: function onStateChange(_ref2) {
        var type = _ref2.type,
          newSelectedItem = _ref2.selectedItem;
        switch (type) {
          // @ts-expect-error This falltrough is wanted
          case useSelect.stateChangeTypes.ToggleButtonBlur:
            if (!selectOnBlur) break;
          case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter: // eslint-disable-line no-fallthrough
          case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
          case useSelect.stateChangeTypes.ItemClick:
            {
              if (newSelectedItem === undefined) return;
              onChange == null ? void 0 : onChange(newSelectedItem != null ? newSelectedItem : null);
            }
        }
      },
      itemToString: itemToString
    }),
    isOpen = _useSelect.isOpen,
    getItemProps = _useSelect.getItemProps,
    getLabelProps = _useSelect.getLabelProps,
    getMenuProps = _useSelect.getMenuProps,
    getToggleButtonProps = _useSelect.getToggleButtonProps,
    highlightedIndex = _useSelect.highlightedIndex;
  var _useFloating = useFloating({
      whileElementsMounted: function whileElementsMounted(ref, _float, update) {
        return autoUpdate(ref, _float, update);
      },
      placement: 'bottom-start',
      open: isOpen,
      middleware: [offset(space.extraSmall2), flip()]
    }),
    refs = _useFloating.refs,
    floatingStyles = _useFloating.floatingStyles;
  return React.createElement("div", {
    className: classNames('eds-dropdown__wrapper', className, {
      'eds-dropdown__wrapper--has-tooltip': labelTooltip !== undefined
    }),
    style: style
  }, React.createElement(BaseFormControl, _extends({
    append: React.createElement(FieldAppend$1, {
      ariaHiddenToggleButton: true,
      ariaLabelCloseList: ariaLabelCloseList,
      ariaLabelOpenList: ariaLabelOpenList,
      clearable: clearable,
      labelClearSelectedItems: labelClearSelectedItem,
      focusable: false,
      getToggleButtonProps: getToggleButtonProps,
      isOpen: isOpen,
      loading: loading,
      loadingText: loadingText,
      onClear: function onClear() {
        var _toggleButtonRef$curr;
        onChange == null ? void 0 : onChange(null);
        (_toggleButtonRef$curr = toggleButtonRef.current) == null ? void 0 : _toggleButtonRef$curr.focus();
      },
      disabled: readOnly || disabled,
      selectedItems: [selectedItem]
    }),
    className: classNames('eds-dropdown', {
      'eds-dropdown--not-filled': !isFilled
    }),
    disabled: disabled,
    disableLabelAnimation: disableLabelAnimation,
    feedback: feedback,
    isFilled: isFilled,
    label: label,
    labelId: getLabelProps().id,
    labelProps: getLabelProps(),
    labelTooltip: labelTooltip,
    prepend: prepend,
    readOnly: readOnly,
    ref: refs.setReference,
    variant: variant
  }, rest), React.createElement("div", _extends({
    className: "eds-dropdown__selected-item"
  }, getToggleButtonProps({
    id: undefined,
    onKeyDown: function onKeyDown(e) {
      if (selectOnTab && isOpen && e.key === 'Tab') {
        // we don't want to clear selection with tab
        var highlitedItem = normalizedItems[highlightedIndex];
        if (highlitedItem) {
          onChange == null ? void 0 : onChange(highlitedItem);
        }
      }
    },
    ref: toggleButtonRef
  })), (_ref3 = (_selectedItem$label = selectedItem == null ? void 0 : selectedItem.label) != null ? _selectedItem$label : React.createElement("div", {
    className: classNames('eds-dropdown__selected-item__placeholder', {
      'eds-dropdown__selected-item__placeholder--readonly': readOnly
    })
  }, placeholder)) != null ? _ref3 : '')), React.createElement(DropdownList, {
    ariaLabelChosenSingular: ariaLabelChosenSingular,
    ariaLabelSelectedItem: ariaLabelSelectedItem,
    getItemProps: getItemProps,
    getMenuProps: getMenuProps,
    highlightedIndex: highlightedIndex,
    isOpen: isOpen,
    listItems: normalizedItems,
    listStyle: _extends({}, floatingStyles, listStyle),
    listRef: refs.setFloating,
    loading: loading,
    loadingText: loadingText,
    selectedItems: selectedItem !== null ? [selectedItem] : []
  }));
};
var _excluded = ["className", "disabled", "disableLabelAnimation", "feedback", "items", "label", "loadingText", "onChange", "prepend", "readOnly", "selectedItem", "style", "value", "variant"];
/** @deprecated use variant="negative" instead */
var error = 'error';
var NativeDropdown = function NativeDropdown(_ref) {
  var _ref2;
  var className = _ref.className,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    disableLabelAnimation = _ref.disableLabelAnimation,
    feedback = _ref.feedback,
    items = _ref.items,
    label = _ref.label,
    loadingText = _ref.loadingText,
    _onChange = _ref.onChange,
    prepend = _ref.prepend,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    selectedItem = _ref.selectedItem,
    style = _ref.style,
    value = _ref.value,
    variant = _ref.variant,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var _useResolvedItems = useResolvedItems(items),
    normalizedItems = _useResolvedItems.items,
    loading = _useResolvedItems.loading;
  var nativeDropdownId = useRandomId('eds-dropdown-native');
  return React.createElement(BaseFormControl, {
    disabled: disabled,
    readOnly: readOnly,
    prepend: prepend,
    append: React.createElement(FieldAppend, {
      hidden: disabled || readOnly,
      loading: loading,
      loadingText: loadingText
    }),
    className: className,
    style: style,
    label: label,
    labelId: nativeDropdownId,
    variant: variant,
    feedback: feedback,
    disableLabelAnimation: disableLabelAnimation,
    isFilled: true
  }, React.createElement("select", _extends({
    "aria-invalid": variant === 'negative' || variant === error,
    "aria-labelledby": nativeDropdownId,
    "aria-busy": loading,
    className: "eds-form-control eds-dropdown--native",
    disabled: disabled || readOnly,
    onChange: function onChange(event) {
      var _normalizedItems$find;
      _onChange == null ? void 0 : _onChange({
        value: event.target.value,
        selectedItem: (_normalizedItems$find = normalizedItems.find(function (item) {
          return item.value === event.target.value;
        })) != null ? _normalizedItems$find : null,
        target: event.target
      });
    },
    value: (_ref2 = value != null ? value : selectedItem == null ? void 0 : selectedItem.value) != null ? _ref2 : undefined
  }, rest), normalizedItems.map(function (item) {
    return React.createElement("option", {
      key: item.value,
      value: item.value
    }, item.label);
  })));
};
var FieldAppend = function FieldAppend(_ref3) {
  var loading = _ref3.loading,
    loadingText = _ref3.loadingText,
    hidden = _ref3.hidden;
  if (loading) {
    return React.createElement("div", {
      className: "eds-dropdown-native__loading-dots"
    }, React.createElement(LoadingDots, {
      "aria-label": loadingText
    }));
  }
  if (hidden) {
    return React.createElement(React.Fragment, null);
  }
  return React.createElement(DownArrowIcon, {
    inline: true
  });
};
warnAboutMissingStyles('dropdown', 'form', 'a11y', 'chip');
export { Dropdown, DropdownDeprecated, MultiSelect, MultiSelectDeprecated, NativeDropdown, SearchableDropdown };